// DashboardView module — extracted from deskte-full.jsx (refactor #8).
// Includes Dashboard (the main landing page), HolidayGreetingsPanel
// (auto-greeting widget), and TeamDoneBadge (small "done by user" pill).

import { useState, useEffect, useMemo } from 'react';
import {
  Users, FileText, Bell, Shield, Search, Plus, Edit2, Trash2,
  ChevronRight, Upload, Send, CheckCircle, X, Save, Eye,
  LogOut, Globe, MoreVertical, TrendingUp, Folder, Mail, Phone,
  Calendar, Hash, Building, AlertTriangle, ArrowLeft, Link2,
  Copy, Check, ListTodo, UserCircle, Crown, UserPlus, Flag,
  ClipboardList, CheckSquare, Circle, UserCog, Heart, Smile,
  FolderOpen, RefreshCw, FileSignature, ExternalLink, Gift,
  DollarSign, BarChart2, ClipboardCheck, Star, Lock, Unlock,
  PenTool, Receipt, ChevronDown, Layers, Activity, Clock,
  Download, EyeOff, Paperclip, XCircle, CreditCard, MessageSquare,
  ChevronLeft, Edit3, UserX, Megaphone, Settings, Ticket,
} from 'lucide-react';
import { Modal, Input, Select, Textarea, Avatar, StatCard, InfoBlock, confirmDialog } from './ui.jsx';
import DateField from './DateField.jsx';
import { uid, fmt, daysUntil, fmtMoney, fmtMonth, MONTHS_LIST, holidayDateForYear, presentingAgentName } from '../utils.js';
import { POLICY_TYPES, getPolicyType, hasCurrentSpouse } from '../policyTypes.js';
import { bdayPersonKey, birthdayDayKey, bdayGreetingKey, defaultGreetingScheduleAt, localDayKey, borrowedEmailNotice, plantillaVivaDelSaludo } from '../greetings.js';
import { useGreetingQueue } from '../greetingQueue.js';
import UnscheduleGreetingButton from './UnscheduleGreetingButton.jsx';
import { useLocalStorage } from '../hooks.js';
import { calcTotals as invoiceTotals, computeStatus as invoiceStatus } from './InvoicesView.jsx';
import { sendClientEmail, createScheduledSend, listActivity } from '../api.js';
import { toast } from './Toast.jsx';
import { enviarSmsAlCliente } from '../enviarSms.js';
import DateTimeField from './DateTimeField.jsx';

// ── File-internal helpers (mirrors of helpers in deskte-full.jsx) ──

// Channel icons for the inline birthday/holiday "send greeting" buttons.
// Defined here (same shape as ClientDetail.jsx + deskte-full.jsx) because
// when this view was extracted in refactor #8 the constant was left
// behind as a dangling reference — usages at lines 191/213/232 threw
// "CHANNEL_BTNS is not defined" the first time a row with an upcoming
// holiday rendered. Keeping the constant local (rather than centralizing
// it into utils) so this file doesn't grow new import edges during the
// fix; a real consolidation can happen later when the refactor revisits
// shared UI atoms.
const CHANNEL_BTNS = [
  { id: 'email', icon: '📧', color: '#0099cc' },
  { id: 'whatsapp', icon: '💬', color: '#25d366' },
  { id: 'sms', icon: '📱', color: '#ffb700' },
];

// Parse a DOB to a LOCAL-midnight Date. Handles both 'YYYY-MM-DD' and slashed
// 'MM/DD/YYYY' (inline-typed spouse/dependent dobs, seed data) — the old
// length-10 concat produced "Invalid Date" on slashed dates, silently dropping
// that person's birthday/26/65 alerts. Mirrors deskte-full.jsx parseDob.
const parseDob = (dob) => {
  if (!dob) return new Date(NaN);
  const s = String(dob).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
  return new Date(s);
};
// LOCAL calendar date (YYYY-MM-DD) — NOT toISOString(), which is UTC and
// rolls to "tomorrow" in the evening for users behind UTC (e.g. a Florida
// agent at 8 PM ET is already the next day in UTC). The activity feed
// buckets by the user's wall-clock day, so it must use local components.
const localDay = (d) => {
  const x = d == null ? new Date() : new Date(d);
  if (isNaN(x.getTime())) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const birthdayToday = (dob) => {
  if (!dob) return false;
  const today = new Date(); const b = parseDob(dob);
  return today.getMonth() === b.getMonth() && today.getDate() === b.getDate();
};
const getAge = (dob) => {
  if (!dob) return null;
  const today = new Date(); const b = parseDob(dob);
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
};
const willTurnAge = (dob, targetAge) => {
  if (!dob) return null;
  const b = parseDob(dob);
  const targetDate = new Date(b.getFullYear() + targetAge, b.getMonth(), b.getDate());
  const today = new Date();
  return Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
};
const daysUntilBirthday = (dob) => {
  if (!dob) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const b = parseDob(dob);
  let nb = new Date(today.getFullYear(), b.getMonth(), b.getDate());
  nb.setHours(0, 0, 0, 0);
  if (nb < today) nb.setFullYear(nb.getFullYear() + 1);
  return Math.round((nb - today) / (1000 * 60 * 60 * 24));
};

// Saludo por WhatsApp: se abre wa.me con el texto puesto, porque no hay
// proveedor de WhatsApp conectado y esa ES la via correcta — el mensaje sale
// de la cuenta de la agente.
//
// El SMS ya NO pasa por aqui: va por `enviarSmsAlCliente` (Quo / Twilio), que
// lo manda desde el numero de la agencia y lo deja en el hilo del cliente. El
// correo tampoco: va por `sendClientEmail`.
function sendGreeting(channel, client, subject, body) {
  const phone = (client.phone || '').replace(/[^0-9+]/g, '');
  const waPhone = phone.startsWith('+') ? phone.replace('+', '') : (phone.startsWith('1') ? phone : '1' + phone);
  if (channel === 'whatsapp' && phone) {
    window.open('https://wa.me/' + waPhone + '?text=' + encodeURIComponent(body), '_blank', 'noopener,noreferrer');
  }
}

function Badge({ status, t, type = 'client' }) {
  const map = { active: 'dkt-bdg dkt-bdg-grn', pending: 'dkt-bdg dkt-bdg-amb', inactive: 'dkt-bdg dkt-bdg-dim', prospect: 'dkt-bdg dkt-bdg-cyn', referral: 'dkt-bdg dkt-bdg-ref' };
  const cls = map[status] || 'dkt-bdg dkt-bdg-dim';
  const label = t.statuses[status] || status;
  return (
    <span className={cls}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
      {label}
    </span>
  );
}

function CancelledPoliciesView({ policies, clients, lang, t }) {
  const pp = (policies || []).filter(p => p.status === 'inactive' || p.status === 'cancelled');
  return (
    <div style={{ padding: '1rem' }}>
      {pp.map(p => {
        const c = (clients || []).find(cl => cl.id === p.clientId);
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.65rem .875rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, marginBottom: '.4rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>{c ? `${c.firstName} ${c.lastName}` : '--'}</div>
              <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>{p.carrier || '--'} · {p.type || '--'}</div>
            </div>
            <span style={{ fontSize: '.68rem', background: 'rgba(255,61,107,.1)', color: 'var(--c-red,#ff3d6b)', padding: '.2rem .5rem', borderRadius: 5, fontWeight: 700 }}>Cancelada</span>
          </div>
        );
      })}
    </div>
  );
}

export function TeamDoneBadge({ done, by, at, lang }) {
  if (!done) return null;
  const es = lang === "es";
  const when = new Date(at).toLocaleDateString("en-US", {month:"short", day:"numeric", hour:"numeric", minute:"2-digit"});
  return <span style={{
    fontSize:".62rem", padding:"2px 7px", borderRadius:5,
    background:"var(--c-green-dim,rgba(0,255,136,.1))",
    color:"var(--c-green,#00ff88)", fontWeight:700, whiteSpace:"nowrap",
    display:"inline-flex", alignItems:"center", gap:3,
    border:"1px solid rgba(0,255,136,.25)"
  }}>✅ {es?"Enviado por":"Sent by"} {by} · {when}</span>;
}


export function HolidayGreetingsPanel({ clients, currentUser, lang, holidayTemplates, emailTemplates, hasTeamActionBeenDone, teamActionKey, logTeamAction, forgetTeamAction, confirmBeforeResend }) {
  const es = lang==="es";
  const holidays = useMemo(()=>{
    // US_HOLIDAYS no existe en este archivo (se quedo en deskte-full.jsx, que
    // es quien siembra holidayTemplates con el): sin plantillas, lista vacia.
    const src = holidayTemplates && holidayTemplates.length ? holidayTemplates : [];
    const today = new Date(); today.setHours(0,0,0,0);
    const yr = today.getFullYear();
    return src.map(h => {
      let d = holidayDateForYear(h, yr);
      if(d < today) d = holidayDateForYear(h, yr+1);
      const diff = Math.ceil((d - today)/(1000*60*60*24));
      return { ...h, date:d, daysUntil:diff, dateStr:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}` };
    }).sort((a,b) => a.daysUntil - b.daysUntil);
  },[holidayTemplates]);
  const nextHol = holidays[0];
  // Birthdays cover the WHOLE household, not just the primary: the spouse and
  // each dependent are checked too. Each match becomes a client-like entry that
  // keeps the REAL client.id (so the email/scheduled-send still works) but
  // carries the birthday person's name + contact, plus a `_bkey` so "Sent"
  // dedup is per-person and a `_rel` label for the UI.
  // `_bkey` (canonical person key) + `_bdayKey` (LOCAL date of the birthday
  // occurrence) are the pair every screen dedups greetings on — ../greetings.js
  // owns both, so Alertas, the login popup and Eventos del mes now agree with
  // this panel instead of each inventing a key (ticket 2026-08-14).
  const householdBdays = (predicate) => {
    const out = [];
    for (const c of clients) {
      if (predicate(c.dob)) out.push({ ...c, _bkey: bdayPersonKey(c.id), _bdayKey: birthdayDayKey(c.dob) });
      const sp = c.spouse;
      // hasCurrentSpouse, not a bare `sp.firstName`: an ex-spouse left on the
      // record still has a name, and Alertas/the popup already filter that way
      // — greeting one here but not there is another red-vs-green mismatch.
      if (hasCurrentSpouse(c) && predicate(sp.dob)) {
        // `_ownEmail` guarda el correo PROPIO de esta persona: vacío significa
        // que el saludo va prestado al del titular, y la pantalla lo advierte.
        out.push({ ...c, firstName: sp.firstName, lastName: sp.lastName || c.lastName, email: sp.email || c.email, _ownEmail: sp.email || '', phone: sp.phone || c.phone, preferredLang: sp.preferredLang || c.preferredLang, dob: sp.dob, _bkey: bdayPersonKey(c.id, 'spouse'), _bdayKey: birthdayDayKey(sp.dob), _rel: es ? 'cónyuge' : 'spouse', _ofName: `${c.firstName} ${c.lastName}`.trim() });
      }
      (Array.isArray(c.dependents) ? c.dependents : []).forEach((d, i) => {
        if (d && d.firstName && predicate(d.dob)) {
          out.push({ ...c, firstName: d.firstName, lastName: d.lastName || c.lastName, email: d.email || c.email, _ownEmail: d.email || '', phone: d.phone || c.phone, dob: d.dob, _bkey: bdayPersonKey(c.id, 'dep', d.id || i), _bdayKey: birthdayDayKey(d.dob), _rel: es ? 'dependiente' : 'dependent', _ofName: `${c.firstName} ${c.lastName}`.trim() });
        }
      });
    }
    return out;
  };
  const bdayClients = householdBdays((dob) => birthdayToday(dob));
  const tomorrowBday = householdBdays((dob) => dob && daysUntilBirthday(dob) === 1);
  // Cola de saludos pendientes de la agencia: lo que permite ofrecer
  // "Desprogramar" en una tarjeta que ya está en verde.
  const greetingQueue = useGreetingQueue();
  // Filas en cola para esta persona/este cumpleaños. Se busca por CLIENTE
  // porque así se encolan; si un hogar tiene dos (titular y cónyuge con correo
  // propio), el diálogo dice cuántas se van a cancelar.
  const queuedFor = (c) => greetingQueue.find(c.id, 'birthday_greeting', c._bdayKey);
  const [sentBday, setSentBday] = useState({});
  const [sentHol, setSentHol] = useState({});
  const [expandHol, setExpandHol] = useState(false);
  // Preview modal state. Open when the user clicks 📧 / 💬 / 📱 on a
  // greeting card. The modal lets them eyeball + edit subject/body
  // before the send actually happens, so they're not surprised by
  // whatever default template fired (and they have a record of what
  // went out). Set to null when closed.
  //
  // Shape: { channel, client, subject, body, kind: 'bday'|'holiday',
  //   holidayId? }
  const [previewModal, setPreviewModal] = useState(null);
  const [sending, setSending] = useState(false);
  // Optional date+time to defer the send. Empty string = send now. The
  // same shape the Campaigns view uses (datetime-local value → ISO).
  const [scheduleAt, setScheduleAt] = useState('');

  const fillTemplate = (tmpl, client) => {
    return (tmpl||"").replace(/\{\{firstName\}\}/g, client.firstName||"").replace(/\{\{lastName\}\}/g, client.lastName||"").replace(/\{\{agentName\}\}/g, presentingAgentName(currentUser));
  };
  // Build the channel button click handler. Instead of immediately
  // sending (the old behavior, which fired mailto: links and marked
  // "Sent" without confirming anything), open the preview modal with
  // the template pre-filled so the user can review/edit + send
  // intentionally.
  // Initial message language: honor the client's preferredLang first
  // (Lissette has English + Spanish clients side by side and the UI lang
  // shouldn't dictate what the CLIENT receives), fall back to UI lang.
  const initialMsgLang = (client) => {
    if (client?.preferredLang === 'spanish') return 'es';
    if (client?.preferredLang === 'english') return 'en';
    if (client?.preferredLanguage === 'es') return 'es';
    if (client?.preferredLanguage === 'en') return 'en';
    return lang === 'es' ? 'es' : 'en';
  };
  // Re-render template body/subject for a given language. Used on initial
  // open AND when the user flips the EN/ES toggle in the modal.
  const fillBdayTemplate = (client, ml) => {
    // EMAIL_TEMPLATE_DEFAULTS vive en deskte-full.jsx y aqui no estaba
    // definido: sin plantillas cargadas esto reventaba. La app siempre pasa
    // emailTemplates (sembrado con esos mismos valores), asi que el respaldo
    // honesto es una plantilla vacia, no un ReferenceError.
    const tpl = (emailTemplates||[]).find(t=>t.id==="et4") || {};
    const isEs = ml === 'es';
    return {
      subject: fillTemplate((isEs?tpl.subjectEs:tpl.subject) || '', client),
      body: fillTemplate((isEs?tpl.bodyEs:tpl.body) || '', client),
    };
  };
  const fillHolTemplate = (hol, client, ml) => {
    const isEs = ml === 'es';
    return {
      subject: fillTemplate(isEs?hol.subjectEs:hol.subject, client),
      body: fillTemplate(isEs?hol.bodyEs:hol.body, client),
    };
  };
  const openBdayPreview = (channel, client) => {
    if (channel === 'email' && !client.email) {
      toast.error(es ? 'Este cliente no tiene email' : 'This client has no email');
      return;
    }
    if ((channel === 'whatsapp' || channel === 'sms') && !client.phone) {
      toast.error(es ? 'Este cliente no tiene teléfono' : 'This client has no phone');
      return;
    }
    const ml = initialMsgLang(client);
    const { subject, body } = fillBdayTemplate(client, ml);
    // Pre-fill 9:00 AM on the birthday when it hasn't arrived yet. The "MAÑANA
    // cumple años" card opened this modal with an EMPTY ⏰ field, so the green
    // button sent the greeting a day early ("varios de esos también se
    // dispararon hoy antes de tiempo", ticket 2026-08-14). Empty for a birthday
    // that is today — those are meant to go out now.
    setScheduleAt(channel === 'email' ? defaultGreetingScheduleAt(client._bdayKey) : '');
    setPreviewModal({ channel, client, subject, body, kind: 'bday', msgLang: ml });
  };
  // Expose the birthday composer so the dashboard's "Pending alerts" panel can
  // open the send/SCHEDULE popup when a birthday alert is clicked — instead of
  // navigating to the client (Lissette 2026-06-19, foto 2). window.__mag*-style
  // globals are the established cross-widget trigger idiom here. If this panel
  // is hidden, the global is never set and the alert click falls back to
  // navigation gracefully. No deps array → always re-binds the latest closure.
  useEffect(() => {
    window.__dktOpenBdayCompose = (client) => openBdayPreview('email', client);
    return () => { if (window.__dktOpenBdayCompose) delete window.__dktOpenBdayCompose; };
  });
  const openHolPreview = (channel, hol, client, targets) => {
    if (channel === 'email' && !client.email) {
      toast.error(es ? 'Este cliente no tiene email' : 'This client has no email');
      return;
    }
    if ((channel === 'whatsapp' || channel === 'sms') && !client.phone) {
      toast.error(es ? 'Este cliente no tiene teléfono' : 'This client has no phone');
      return;
    }
    const ml = initialMsgLang(client);
    const { subject, body } = fillHolTemplate(hol, client, ml);
    setScheduleAt('');
    // `holidayTargets` = every client this greeting covers (all active clients
    // for a "send to all", or just this one for a single send). Used by
    // confirmSend to mark the whole batch as Sent AFTER the send succeeds.
    setPreviewModal({ channel, client, subject, body, kind: 'holiday', holidayId: hol.id, holidayRef: hol, msgLang: ml, holidayTargets: Array.isArray(targets) && targets.length ? targets : [client.id] });
  };
  // Toggle handler: re-fill subject + body from the OTHER language's
  // template. Overwrites any in-flight edits — keep it explicit so the
  // user knows clicking the flag swaps the template.
  const switchMsgLang = (nextLang) => {
    setPreviewModal(p => {
      if (!p || p.msgLang === nextLang) return p;
      const filled = p.kind === 'bday'
        ? fillBdayTemplate(p.client, nextLang)
        : fillHolTemplate(p.holidayRef, p.client, nextLang);
      return { ...p, msgLang: nextLang, subject: filled.subject, body: filled.body };
    });
  };
  // Actually send. For email we POST to /clients/:id/emails so the
  // message goes through the user's connected mailbox (Gmail OAuth /
  // Outlook / SendGrid fallback) AND lands in the client_email_messages
  // table — so it's visible on the client's Emails tab afterward. For
  // WhatsApp/SMS we still open the link out to the local app since
  // there's no integration yet (tracked in pending_sms_whatsapp_calls).
  const confirmSend = async () => {
    if (!previewModal) return;
    const { channel, client, subject, body, kind, holidayId, holidayTargets } = previewModal;
    // Sending NOW for a birthday still days away greets the client early. The
    // ⏰ field is pre-filled for those, so an empty one here means the user
    // cleared it — confirm rather than silently firing a day ahead
    // (ticket 2026-08-14).
    if (kind === 'bday' && channel === 'email' && !scheduleAt && client._bdayKey > localDayKey()) {
      const ok = await confirmDialog(es
        ? `El cumpleaños es el ${client._bdayKey}. Sin fecha en "Programar para", el correo sale AHORA — hoy, no el día del cumpleaños. ¿Enviarlo de todas formas?`
        : `The birthday is on ${client._bdayKey}. With no date in "Schedule for", this email goes out NOW — today, not on their birthday. Send it anyway?`);
      if (!ok) return;
    }
    setSending(true);
    try {
      if (channel === 'email' && scheduleAt) {
        // Defer the send. One ScheduledSend row per recipient via the worker.
        // Source MUST match the backend enum (scheduled-sends.routes.ts):
        // 'birthday_greeting' / 'holiday_greeting' — sending the old
        // 'greeting_bday' values tripped Zod with "Invalid body".
        const runAtIso = new Date(scheduleAt).toISOString();
        // Texto de la plantilla sin tocar → el servidor lo rearma el día del
        // envío con la plantilla y la ficha de ese día. Editado → tal cual.
        const { msgLang, holidayRef } = previewModal;
        const original = kind === 'bday' ? fillBdayTemplate(client, msgLang) : fillHolTemplate(holidayRef, client, msgLang);
        const tplId = kind === 'bday' ? ((emailTemplates || []).find(t => t.id === 'et4') ? 'et4' : null) : holidayId;
        const queued = await createScheduledSend({
          clientId: client.id,
          channel: 'email',
          recipient: client.email,
          subject,
          body,
          runAt: runAtIso,
          source: kind === 'bday' ? 'birthday_greeting' : 'holiday_greeting',
          ...plantillaVivaDelSaludo({
            sinTocar: subject === original.subject && body === original.body,
            templateId: tplId, tipo: kind === 'bday' ? 'bday' : 'holiday', lang: msgLang,
            personKey: client._bkey || client.id,
          }),
        });
        const at = new Date(scheduleAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
        // One greeting per recipient per day is enforced server-side, so this
        // may be reporting a row a teammate already queued. Saying "programado"
        // for something already handled is how people end up sending twice by
        // hand to "make sure".
        if (queued?._duplicate) {
          toast.info(es
            ? 'Este cliente ya tenía su saludo programado para ese día — no se duplicó.'
            : 'This client already had their greeting scheduled for that day — nothing was duplicated.');
        } else {
          toast.success(es ? `📅 Programado para ${at}` : `📅 Scheduled for ${at}`);
        }
      } else if (channel === 'email') {
        // Convert plain-text body to minimal HTML — paragraphs from
        // double-newlines, single-newlines become <br>. The backend
        // also stores `text` for the plain-text alt part.
        const html = body
          .split(/\n\n+/)
          .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
          .join('');
        // Etiqueta el correo como saludo para que cuente en el control de
        // repetidos, que lee la cola de envíos y no ve lo que sale al momento.
        await sendClientEmail(client.id, {
          subject, html, text: body,
          source: kind === 'bday' ? 'birthday_greeting' : 'holiday_greeting',
        });
        toast.success(es ? `✅ Enviado a ${client.email}` : `✅ Sent to ${client.email}`);
      } else if (channel === 'sms') {
        // Sale por Deskte (Quo / Twilio). Si no se puede, `enviarSmsAlCliente`
        // abre la app de mensajes y dice por que.
        const r = await enviarSmsAlCliente({ clientId: client.id, telefono: client.phone, mensaje: body, lang });
        if (r.enviado) toast.success(es ? `✅ SMS enviado a ${client.phone}` : `✅ SMS sent to ${client.phone}`);
        else if (r.texto) toast.warn(r.texto);
        else toast.success(es ? 'Abierto en tu app de mensajes para enviar' : 'Opened in your messaging app to send');
      } else if (channel === 'whatsapp') {
        // No hay proveedor de WhatsApp: se abre wa.me con el texto puesto.
        sendGreeting(channel, client, subject, body);
        toast.success(es ? 'Abierto en WhatsApp para enviar' : 'Opened in WhatsApp to send');
      }
      // Mark sent only after the operation actually completed without
      // throwing. Stale "Sent" badges were the original UX bug.
      if (kind === 'bday') {
        setSentBday(p => ({ ...p, [client._bkey || client.id]: true }));
      } else {
        // Mark every covered client as Sent so the holiday card flips to
        // "✅ Enviado" and stops showing the send buttons.
        const targets = (Array.isArray(holidayTargets) && holidayTargets.length) ? holidayTargets : [client.id];
        setSentHol(p => { const next = { ...p }; for (const cid of targets) next[holidayId + '_' + cid] = true; return next; });
      }
      if (logTeamAction) {
        const meta = kind === 'bday'
          // The marker belongs to the BIRTHDAY, not to the day the button was
          // pressed: scheduling tomorrow's greeting today must still read as
          // done tomorrow, or the card comes back red and gets re-sent.
          ? { kind: 'bday', channel, forDate: client._bdayKey }
          : { kind: 'holiday', channel, holidayId };
        // Per-person key for household birthdays; real client.id otherwise.
        logTeamAction('greeting_sent', kind === 'bday' ? (client._bkey || client.id) : client.id, meta);
        // Holidays go to everyone, so also write a stable per-holiday signal
        // (entity `holiday_<id>`) — that's what holDone reads back so "Enviado"
        // survives a reload and is shared with the team (mirrors birthdays).
        if (kind !== 'bday' && holidayId) {
          logTeamAction('greeting_sent', 'holiday_' + holidayId, { kind: 'holiday' });
        }
      }
      setPreviewModal(null);
    } catch (err) {
      toast.error((es ? 'Error: ' : 'Error: ') + (err?.message || 'unknown'));
    } finally {
      setSending(false);
    }
  };
  // Back-compat shims for the JSX handlers below. The preview modal
  // gates the actual send, so the resend confirmation gate is no
  // longer needed at the button-click step — the user reviews the
  // preview, which is itself an explicit confirmation.
  const sendBday = (channel, client) => openBdayPreview(channel, client);
  const sendHol = (channel, hol, client) => openHolPreview(channel, hol, client);
  const sendHolAll = async (channel, hol) => {
    const active = clients.filter(c=>c.status==="active"&&(channel==="email"?c.email:c.phone));
    if (active.length === 0) {
      toast.error(es ? 'No hay clientes activos con datos de contacto' : 'No active clients with contact info');
      return;
    }
    // Batch mode: open the preview against the first client as a
    // representative sample. The user reviewing knows this template
    // will be sent to all N active clients with this channel. A
    // dedicated batch UI can come later; for now this matches the
    // single-client preview pattern instead of silently firing N
    // mailto: links.
    if (!(await confirmDialog(es
      ? `Esto enviará el saludo a ${active.length} cliente(s) por ${channel}. ¿Continuar?`
      : `This will send the greeting to ${active.length} client(s) via ${channel}. Continue?`))) {
      return;
    }
    // Pass the full recipient list so confirmSend can mark the whole batch
    // as Sent AFTER the send actually goes through (previously it marked
    // them sent the instant the preview opened — so the card flipped to
    // "Sent" even if the user then cancelled the preview).
    openHolPreview(channel, hol, active[0], active.map(c => c.id));
  };

  // "Sent" must survive a refresh AND be visible to the whole team — the
  // local `sentBday` only covers the current tab/session, so after a
  // reload (or for a teammate like a seat) the card wrongly showed the
  // send buttons again even though the email already went out. The
  // persisted, team-shared signal is written by logTeamAction('greeting_sent');
  // ../greetings.js owns the key so every screen asks the same question.
  const bdayDone = (c) => {
    const key = c._bkey || c.id; // per-person for household members
    if (sentBday[key]) return true;
    if (hasTeamActionBeenDone) {
      try { return !!hasTeamActionBeenDone(bdayGreetingKey(key, c._bdayKey)).done; }
      catch (_) { return false; }
    }
    return false;
  };
  // A holiday greeting is "done" once it's been sent for this holiday in this
  // session (sentHol is keyed `<holidayId>_<clientId>`). Lets the card flip to
  // "✅ Enviado" instead of forever showing the send buttons.
  const holDone = (hol) => {
    if (!hol) return false;
    const prefix = hol.id + '_';
    if (Object.keys(sentHol).some(k => sentHol[k] && k.startsWith(prefix))) return true;
    // La cola manda: si ya hay correos encolados para el día del feriado, está
    // programado. La marca de abajo se estampa con el día en que se pulsó el
    // botón, así que caduca a medianoche — y al día siguiente esta tarjeta
    // volvía a ofrecer los botones de enviar sobre un saludo que ya estaba en
    // cola, sin forma de deshacerlo desde aquí.
    if (greetingQueue.findHoliday(hol.dateStr).length) return true;
    // Persisted, team-shared signal so "Enviado" survives a reload (mirrors
    // bdayDone). Written above as entity `holiday_<id>`.
    if (teamActionKey && hasTeamActionBeenDone) {
      try { return !!hasTeamActionBeenDone(teamActionKey('greeting_sent', 'holiday_' + hol.id, { kind: 'holiday' })).done; }
      catch (_) { return false; }
    }
    return false;
  };
  // La marca que pinta el feriado en verde. Misma llamada que la escribe en
  // confirmSend y que la lee holDone — si se re-derivara aquí a mano, borrar
  // dejaría de encontrarla y la tarjeta no volvería nunca.
  const holMarkerKey = (hol) => (teamActionKey
    ? teamActionKey('greeting_sent', 'holiday_' + hol.id, { kind: 'holiday' })
    : null);
  // Verde + "Desprogramar" para un feriado. Sus filas en cola son una POR
  // CLIENTE, así que se buscan por el día del feriado y no por cliente. La
  // función devuelve JSX (no un componente): definir uno aquí dentro lo
  // remontaría en cada render y el botón perdería su estado a media faena.
  const holBadge = (hol, small) => {
    const queued = greetingQueue.findHoliday(hol.dateStr);
    return <span style={{display:'flex',alignItems:'center',gap:'.5rem',flexShrink:0}}>
      <span style={{fontSize:small?'.68rem':'.72rem',color:'var(--c-green,#00ff88)',fontWeight:600}}>
        ✅ {queued.length?(es?"Programado":"Scheduled"):(es?"Enviado":"Sent")}
      </span>
      <UnscheduleGreetingButton rows={queued} kind="holiday" who={es?hol.nameEs:hol.name}
        unschedule={greetingQueue.unschedule} markerKey={holMarkerKey(hol)}
        onDone={()=>{
          setSentHol(p=>{const n={...p};for(const k of Object.keys(n))if(k.startsWith(hol.id+'_'))delete n[k];return n;});
          const key=holMarkerKey(hol); if(key&&forgetTeamAction) forgetTeamAction(key);
        }} lang={lang} compact/>
    </span>;
  };

  const nothingToShow = bdayClients.length===0 && tomorrowBday.length===0 && (!nextHol || nextHol.daysUntil > 7) && !expandHol;

  return <div className="dkt-card" style={{padding:'1.1rem'}}>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.875rem'}}>
      <div style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
        <span style={{fontSize:'1.1rem'}}>🎉</span>
        <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'.9rem',color:'var(--c-white,#eef6ff)'}}>{es?"Saludos y Felicitaciones":"Greetings & Holidays"}</span>
      </div>
      <button onClick={()=>setExpandHol(!expandHol)} style={{background:'none',border:'none',fontSize:'.7rem',color:'var(--c-cyan,#00d4ff)',cursor:'pointer'}}>{expandHol?(es?"Cerrar":"Close"):(es?"Ver Holidays":"View Holidays")} {expandHol?"▲":"▼"}</button>
    </div>

    {/* Birthday alerts. The whole card is clickable → opens the EMAIL composer,
        which carries the optional "⏰ Schedule for" field, so a click anywhere
        lets the user send or SCHEDULE the greeting. The per-channel buttons keep
        working and stopPropagation so they don't double-trigger the card. */}
    {bdayClients.map(c=>{ const done=bdayDone(c); return <div key={c._bkey||c.id}
      onClick={done?undefined:()=>openBdayPreview('email',c)}
      title={done?undefined:(es?"Clic para enviar o programar el correo":"Click to send or schedule the email")}
      onMouseEnter={done?undefined:(e)=>{e.currentTarget.style.borderColor='rgba(244,114,182,.55)';}}
      onMouseLeave={done?undefined:(e)=>{e.currentTarget.style.borderColor='rgba(244,114,182,.2)';}}
      style={{display:'flex',alignItems:'center',gap:'.7rem',padding:'.6rem .85rem',borderRadius:10,background:'linear-gradient(135deg,rgba(244,114,182,.08),rgba(244,114,182,.02))',border:'1px solid rgba(244,114,182,.2)',marginBottom:'.4rem',cursor:done?'default':'pointer',transition:'border-color .12s'}}>
      <span style={{fontSize:'1.2rem'}}>🎂</span>
      <div style={{flex:1}}>
        <div style={{fontSize:'.82rem',fontWeight:700,color:'#f472b6'}}>{es?"¡Hoy cumple años":"Today is birthday of"} {c.firstName} {c.lastName}!</div>
        <div style={{fontSize:'.68rem',color:'var(--c-text2,#7aa3c0)',marginTop:1}}>{c._rel?`${c._rel} · ${c._ofName} · `:''}{c.email || (es ? "Sin email" : "No email")}</div>
        {!done && c.email && <div style={{fontSize:'.62rem',color:'#f472b6',opacity:.85,marginTop:2}}>{es?"Clic para enviar o programar el correo →":"Click to send or schedule the email →"}</div>}
      </div>
      {!done?<div style={{display:'flex',gap:4}}>{CHANNEL_BTNS.map(ch=><button key={ch.id} onClick={(e)=>{e.stopPropagation();sendBday(ch.id,c);}} title={ch.id} style={{padding:'5px 8px',background:`${ch.color}18`,border:`1px solid ${ch.color}30`,color:ch.color,fontSize:'.85rem',borderRadius:7,cursor:'pointer',lineHeight:1}}>{ch.icon}</button>)}</div>
      :<div style={{display:'flex',alignItems:'center',gap:'.5rem',flexShrink:0}}>
        <span style={{fontSize:'.72rem',color:'var(--c-green,#00ff88)',fontWeight:600}}>✅ {queuedFor(c).length?(es?"Programado":"Scheduled"):(es?"Enviado":"Sent")}</span>
        <UnscheduleGreetingButton rows={queuedFor(c)} personKey={c._bkey||c.id} dayKey={c._bdayKey}
          who={`${c.firstName} ${c.lastName}`.trim()} unschedule={greetingQueue.unschedule}
          onDone={()=>{setSentBday(p=>{const n={...p};delete n[c._bkey||c.id];return n;});forgetTeamAction&&forgetTeamAction(bdayGreetingKey(c._bkey||c.id,c._bdayKey));}} lang={lang} compact/>
      </div>}
    </div>; })}

    {tomorrowBday.map(c=>{ const done=bdayDone(c); return <div key={c._bkey||c.id}
      onClick={done?undefined:()=>openBdayPreview('email',c)}
      title={done?undefined:(es?"Clic para enviar o programar el correo":"Click to send or schedule the email")}
      style={{display:'flex',alignItems:'center',gap:'.7rem',padding:'.5rem .85rem',borderRadius:10,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',marginBottom:'.4rem',cursor:done?'default':'pointer'}}>
      <span style={{fontSize:'1rem'}}>🎂</span>
      <div style={{flex:1}}>
        <div style={{fontSize:'.78rem',fontWeight:600,color:'var(--c-text,#c8dff0)'}}>{es?"Mañana cumple años":"Tomorrow is birthday of"} {c.firstName} {c.lastName}{c._rel?` (${c._rel} · ${c._ofName})`:''}</div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'.5rem',flexShrink:0}}>
        <span style={{fontSize:'.65rem',padding:'2px 8px',borderRadius:5,background:'rgba(244,114,182,.1)',color:'#f472b6',fontWeight:600}}>{es?"MAÑANA":"TOMORROW"}</span>
        {/* "Programado", not "Enviado": the greeting is queued for tomorrow.
            Calling it sent is what made this card look wrong the next day. */}
        {!done?<div style={{display:'flex',gap:4}}>{CHANNEL_BTNS.map(ch=><button key={ch.id} onClick={(e)=>{e.stopPropagation();sendBday(ch.id,c);}} title={ch.id} style={{padding:'5px 8px',background:`${ch.color}18`,border:`1px solid ${ch.color}30`,color:ch.color,fontSize:'.85rem',borderRadius:7,cursor:'pointer',lineHeight:1}}>{ch.icon}</button>)}</div>
        :<><span style={{fontSize:'.72rem',color:'var(--c-green,#00ff88)',fontWeight:600}}>✅ {es?"Programado":"Scheduled"}</span>
          <UnscheduleGreetingButton rows={queuedFor(c)} personKey={c._bkey||c.id} dayKey={c._bdayKey}
            who={`${c.firstName} ${c.lastName}`.trim()} unschedule={greetingQueue.unschedule}
            onDone={()=>{setSentBday(p=>{const n={...p};delete n[c._bkey||c.id];return n;});forgetTeamAction&&forgetTeamAction(bdayGreetingKey(c._bkey||c.id,c._bdayKey));}} lang={lang} compact/></>}
      </div>
    </div>; })}

    {/* Upcoming holiday */}
    {nextHol && nextHol.daysUntil <= 7 && <div style={{padding:'.7rem .85rem',borderRadius:10,background:'linear-gradient(135deg,rgba(0,212,255,.06),rgba(0,153,204,.03))',border:'1px solid rgba(0,212,255,.2)',marginBottom:'.4rem'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.4rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
          <span style={{fontSize:'1.3rem'}}>{nextHol.emoji}</span>
          <div>
            <div style={{fontSize:'.85rem',fontWeight:700,color:'var(--c-cyan,#00d4ff)'}}>{es?nextHol.nameEs:nextHol.name}</div>
            <div style={{fontSize:'.68rem',color:'var(--c-text2,#7aa3c0)'}}>{nextHol.daysUntil===0?(es?"¡HOY!":"TODAY!"):nextHol.daysUntil===1?(es?"¡MAÑANA!":"TOMORROW!"):`${es?"en":"in"} ${nextHol.daysUntil} ${es?"días":"days"}`}</div>
          </div>
        </div>
        {holDone(nextHol)
          ? holBadge(nextHol)
          : <div style={{display:'flex',gap:4}}>{CHANNEL_BTNS.map(ch=><button key={ch.id} onClick={()=>sendHolAll(ch.id,nextHol)} title={`${ch.id} all`} style={{padding:'5px 8px',background:`${ch.color}18`,border:`1px solid ${ch.color}30`,color:ch.color,fontSize:'.85rem',borderRadius:7,cursor:'pointer',lineHeight:1}}>{ch.icon}</button>)}</div>}
      </div>
      <div style={{fontSize:'.68rem',color:'var(--c-text3,#45688a)'}}>{es?`Enviar saludo de ${nextHol.nameEs} a todos los clientes activos con email`:`Send ${nextHol.name} greeting to all active clients with email`}</div>
    </div>}

    {nothingToShow && !expandHol && <div style={{textAlign:'center',padding:'.75rem',fontSize:'.78rem',color:'var(--c-text3,#45688a)'}}>
      {es?"Sin cumpleaños próximos. ":"No upcoming birthdays. "}{nextHol&&<span>{es?"Próximo feriado":"Next holiday"}: {nextHol.emoji} {es?nextHol.nameEs:nextHol.name} ({es?"en":"in"} {nextHol.daysUntil} {es?"días":"days"})</span>}
    </div>}

    {/* All holidays list */}
    {expandHol&&<div style={{marginTop:'.5rem'}}>
      <div style={{fontSize:'.75rem',fontWeight:700,color:'var(--c-white,#eef6ff)',marginBottom:'.5rem'}}>{es?"📅 Calendario de Feriados USA":"📅 US Holiday Calendar"}</div>
      <div style={{display:'flex',flexDirection:'column',gap:'.3rem',maxHeight:350,overflowY:'auto'}}>
        {holidays.map(h=><div key={h.id} style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.45rem .7rem',borderRadius:8,background:h.daysUntil<=7?'rgba(0,212,255,.06)':'var(--c-surface2,#112538)',border:`1px solid ${h.daysUntil<=7?'rgba(0,212,255,.15)':'var(--c-border,#1a3550)'}`}}>
          <span style={{fontSize:'1rem',flexShrink:0}}>{h.emoji}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:'.78rem',fontWeight:600,color:h.daysUntil<=7?'var(--c-cyan,#00d4ff)':'var(--c-text,#c8dff0)'}}>{es?h.nameEs:h.name}</div>
            <div style={{fontSize:'.65rem',color:'var(--c-text3,#45688a)'}}>{h.dateStr} — {h.daysUntil===0?(es?"¡HOY!":"TODAY!"):h.daysUntil===1?(es?"¡MAÑANA!":"TOMORROW!"):`${h.daysUntil} ${es?"días":"days"}`}</div>
          </div>
          {holDone(h)
            ? holBadge(h, true)
            : (h.daysUntil<=3&&<div style={{display:'flex',gap:3}}>{CHANNEL_BTNS.map(ch=><button key={ch.id} onClick={()=>sendHolAll(ch.id,h)} title={ch.id} style={{padding:'3px 6px',background:`${ch.color}18`,border:`1px solid ${ch.color}30`,color:ch.color,fontSize:'.75rem',borderRadius:5,cursor:'pointer',lineHeight:1}}>{ch.icon}</button>)}</div>)}
        </div>)}
      </div>
    </div>}
    {/* Preview modal — opens when the user clicks 📧 / 💬 / 📱 on any
        greeting card. They can review/edit subject + body, then send.
        Email goes via /clients/:id/emails (OAuth/SendGrid) and lands in
        the client's email history. WhatsApp/SMS still hands off to the
        local app because there's no backend send for those yet. */}
    {previewModal && (
      <Modal
        title={
          (es ? 'Vista previa — ' : 'Preview — ') +
          (previewModal.channel === 'email' ? '📧 Email' :
            previewModal.channel === 'whatsapp' ? '💬 WhatsApp' : '📱 SMS')
        }
        onClose={() => !sending && setPreviewModal(null)}
        wide
      >
        {/* Message language toggle. Defaults to client's preferredLang
            but the user can flip it per send — Lissette has both English
            and Spanish clients and the UI language shouldn't dictate
            what the client receives. Clicking the OTHER flag re-fills
            the subject + body from that language's template (overwrites
            any in-flight edits — by design, since "switch language" is
            the explicit intent of clicking the toggle). */}
        <div style={{display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'.75rem'}}>
          <span style={{fontSize:'.65rem',color:'var(--c-text3,#45688a)',textTransform:'uppercase',letterSpacing:'.08em'}}>
            {es ? 'Idioma del mensaje' : 'Message language'}
          </span>
          <div style={{display:'flex',gap:4}}>
            <button
              type="button"
              onClick={() => switchMsgLang('en')}
              disabled={sending}
              style={{display:'inline-flex',alignItems:'center',gap:4,padding:'.25rem .55rem',borderRadius:6,
                cursor: sending ? 'wait' : 'pointer',
                border: `1px solid ${previewModal.msgLang === 'en' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
                background: previewModal.msgLang === 'en' ? 'rgba(0,212,255,.1)' : 'var(--c-surface2,#112538)',
                color: previewModal.msgLang === 'en' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
                fontSize:'.7rem',fontWeight:700,fontFamily:'inherit'}}>
              🇺🇸 English
            </button>
            <button
              type="button"
              onClick={() => switchMsgLang('es')}
              disabled={sending}
              style={{display:'inline-flex',alignItems:'center',gap:4,padding:'.25rem .55rem',borderRadius:6,
                cursor: sending ? 'wait' : 'pointer',
                border: `1px solid ${previewModal.msgLang === 'es' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
                background: previewModal.msgLang === 'es' ? 'rgba(0,212,255,.1)' : 'var(--c-surface2,#112538)',
                color: previewModal.msgLang === 'es' ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
                fontSize:'.7rem',fontWeight:700,fontFamily:'inherit'}}>
              🇪🇸 Español
            </button>
          </div>
          {previewModal.client?.preferredLang && (
            <span style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',fontStyle:'italic'}}>
              {es
                ? `(prefiere ${previewModal.client.preferredLang === 'spanish' ? 'español' : 'inglés'})`
                : `(prefers ${previewModal.client.preferredLang === 'spanish' ? 'Spanish' : 'English'})`}
            </span>
          )}
        </div>
        <div style={{marginBottom:'.75rem',padding:'.6rem .8rem',background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:8}}>
          <div style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>
            {es ? 'Para' : 'To'}
          </div>
          <div style={{fontSize:'.85rem',fontWeight:600,color:'var(--c-white,#eef6ff)'}}>
            {previewModal.client.firstName} {previewModal.client.lastName}
            <span style={{fontWeight:400,color:'var(--c-text2,#7aa3c0)',marginLeft:6}}>
              &lt;{previewModal.channel === 'email'
                ? (previewModal.client.email || '—')
                : (previewModal.client.phone || '—')}&gt;
            </span>
          </div>
          {/* Cuando quien cumple es del hogar y no tiene correo propio, esta
              dirección es la del titular. Decirlo: "María <juan@correo.com>"
              se lee como el correo de María si vas con prisa. */}
          {previewModal.channel === 'email' && (() => {
            const nota = borrowedEmailNotice({
              ownEmail: previewModal.client._ownEmail,
              deliverTo: previewModal.client.email,
              personName: [previewModal.client.firstName, previewModal.client.lastName].filter(Boolean).join(' '),
              ownerName: previewModal.client._ofName,
              rel: previewModal.client._rel,
              lang,
            });
            return nota ? (
              <div style={{
                marginTop: 6, padding: '.45rem .6rem', borderRadius: 6,
                background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.28)',
                fontSize: '.68rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.45,
              }}>
                <span style={{ color: '#ffb700', fontWeight: 700, marginRight: 4 }}>⚠</span>{nota}
              </div>
            ) : null;
          })()}
        </div>
        {previewModal.channel === 'email' && (
          <div style={{marginBottom:'.75rem'}}>
            <div style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>
              {es ? 'Asunto' : 'Subject'}
            </div>
            <Input
              value={previewModal.subject}
              onChange={(e) => setPreviewModal(p => ({ ...p, subject: e.target.value }))}
              disabled={sending}
            />
          </div>
        )}
        <div style={{marginBottom:'1rem'}}>
          <div style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>
            {es ? 'Mensaje' : 'Message'}
          </div>
          <Textarea
            value={previewModal.body}
            onChange={(e) => setPreviewModal(p => ({ ...p, body: e.target.value }))}
            rows={8}
            disabled={sending}
          />
        </div>
        {previewModal.channel === 'email' && (
          <div style={{marginBottom:'.75rem',padding:'.6rem .75rem',background:'var(--c-surface2,#112538)',border:'1px dashed var(--c-border2,#1f3f60)',borderRadius:9}}>
            <label style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',display:'block',marginBottom:4}}>
              ⏰ {es ? 'Programar para (opcional)' : 'Schedule for (optional)'}
            </label>
            <DateTimeField
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              disabled={sending}
              lang={es ? 'es' : 'en'}
            />
            {scheduleAt ? (
              <div style={{fontSize:'.62rem',color:'var(--c-amber,#ffb700)',marginTop:4}}>
                {es ? '📅 Se enviará automáticamente en esa fecha/hora.' : '📅 Will send automatically at that date/time.'}
              </div>
            ) : previewModal.kind === 'bday' && previewModal.client?._bdayKey > localDayKey() && (
              <div style={{fontSize:'.62rem',color:'var(--c-red,#ff3d6b)',marginTop:4}}>
                {es ? `⚠️ Sin fecha, el correo sale HOY — el cumpleaños es el ${previewModal.client._bdayKey}.` : `⚠️ With no date the email goes out TODAY — the birthday is ${previewModal.client._bdayKey}.`}
              </div>
            )}
          </div>
        )}
        {previewModal.channel === 'email' && (
          <div style={{fontSize:'.68rem',color:'var(--c-text3,#45688a)',marginBottom:'.75rem',fontStyle:'italic'}}>
            {es
              ? 'Se enviará desde tu cuenta de Deskte (Gmail/Outlook conectada) y aparecerá en la pestaña Emails del cliente.'
              : 'Will send from your connected Deskte mailbox (Gmail/Outlook) and appear in this client\'s Emails tab.'}
          </div>
        )}
        {(previewModal.channel === 'whatsapp' || previewModal.channel === 'sms') && (
          <div style={{fontSize:'.68rem',color:'var(--c-text3,#45688a)',marginBottom:'.75rem',fontStyle:'italic'}}>
            {es
              ? `Esto abrirá ${previewModal.channel === 'whatsapp' ? 'WhatsApp' : 'tu app de SMS'} con el mensaje listo. Tendrás que darle "Enviar" ahí para que salga.`
              : `This will open ${previewModal.channel === 'whatsapp' ? 'WhatsApp' : 'your SMS app'} with the message ready. You'll need to click Send there for it to go out.`}
          </div>
        )}
        <div style={{display:'flex',gap:'.5rem',justifyContent:'flex-end'}}>
          <button
            type="button"
            onClick={() => setPreviewModal(null)}
            disabled={sending}
            className="dkt-btn-g"
          >
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={confirmSend}
            disabled={sending || !previewModal.subject || !previewModal.body}
            style={{display:'inline-flex',alignItems:'center',gap:5,background:'linear-gradient(135deg,#059669,#10b981)',border:'none',color:'#fff',fontSize:'.82rem',fontWeight:800,padding:'.55rem 1.4rem',borderRadius:9,cursor:sending?'wait':'pointer',fontFamily:'inherit',opacity:sending?0.7:1}}
          >
            {sending
              ? (scheduleAt && previewModal.channel === 'email'
                  ? (es ? 'Programando…' : 'Scheduling…')
                  : (es ? 'Enviando…' : 'Sending…'))
              : previewModal.channel === 'email'
                ? (scheduleAt
                    ? (es ? '📅 Programar envío' : '📅 Schedule send')
                    : (es ? '✉️ Enviar por Deskte' : '✉️ Send via Deskte'))
                : previewModal.channel === 'whatsapp'
                  ? (es ? '💬 Abrir WhatsApp' : '💬 Open WhatsApp')
                  : (es ? '📱 Abrir SMS' : '📱 Open SMS')}
          </button>
        </div>
      </Modal>
    )}
  </div>;
}


// Toggleable widget ids on the dashboard. The top stat cards are always
// visible (KPIs at a glance); the larger panels can be hidden via a
// settings menu and their visibility is persisted in localStorage.
const DASHBOARD_WIDGETS = [
  { id: 'recent_clients',  labelEn: 'Recent Clients',          labelEs: 'Clientes Recientes' },
  { id: 'pending_alerts',  labelEn: 'Pending Alerts',          labelEs: 'Alertas Pendientes' },
  { id: 'greetings',       labelEn: 'Greetings & Holidays',    labelEs: 'Saludos y Feriados' },
  { id: 'recent_changes',  labelEn: 'Recent Changes',          labelEs: 'Cambios Recientes' },
  { id: 'activity_feed',   labelEn: 'Activity Feed',           labelEs: 'Feed de Actividad' },
];

export function Dashboard({ clients, policies, docs, tasks, currentUser, users, setView, changeHistory, activitylog, eligibilityAlerts, holidayTemplates, emailTemplates, invoices, ticketsOpen = 0, fuOverdue = 0, hasTeamActionBeenDone, teamActionKey, logTeamAction, forgetTeamAction, confirmBeforeResend, t, lang, userPrefs = {}, onPrefsChange }) {
  const td=t.dashboard;
  // Per-user widget visibility — defaults to all on. The user can hide
  // widgets they don't care about via the ⚙️ button. Stored as an array
  // of hidden ids (smaller payload than a full visible/hidden map).
  // Per-user widget visibility — now stored on the server in uiPrefs so it
  // follows the user across devices (was localStorage-only). Falls back to []
  // when prefs aren't wired (e.g. demo mode).
  const hiddenWidgets = Array.isArray(userPrefs?.dashboardHiddenWidgets) ? userPrefs.dashboardHiddenWidgets : [];
  const setHiddenWidgets = (updater) => {
    if (!onPrefsChange) return;
    onPrefsChange((p) => {
      const cur = Array.isArray(p?.dashboardHiddenWidgets) ? p.dashboardHiddenWidgets : [];
      const next = typeof updater === 'function' ? updater(cur) : updater;
      return { ...p, dashboardHiddenWidgets: next };
    });
  };
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const isHidden = (id) => Array.isArray(hiddenWidgets) && hiddenWidgets.includes(id);
  const toggleWidget = (id) => {
    setHiddenWidgets((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
    });
  };
  const resetWidgets = () => setHiddenWidgets([]);
  // Invoices were hoisted to deskte-full when we made them server-backed
  // (2026-05-20); fall back to the legacy localStorage path for demo mode.
  const [localInvoices] = useLocalStorage('dkt6_invoices', []);
  const storedInvoices = invoices ?? localInvoices;
  const invoiceKpis = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    let outstanding = 0;
    let paidThisMonth = 0;
    let overdueCount = 0;
    for (const inv of storedInvoices) {
      const t = invoiceTotals(inv);
      const s = invoiceStatus(inv);
      if (s === 'void' || s === 'refunded' || s === 'draft') continue;
      if (s === 'paid') {
        // Bucket each payment by the LOCAL month it was recorded — not the
        // whole invoice total by its latest payment's month. recordedAt is a
        // UTC timestamp, so compare its local-month (feedback_local_day_not_utc)
        // and only sum payments that actually landed this month.
        for (const p of (inv.payments || [])) {
          const localMonth = (localDay(p.recordedAt) || '').slice(0, 7);
          if (localMonth === thisMonth) paidThisMonth += parseFloat(p.amount || 0);
        }
        continue;
      }
      // sent / viewed / partial / overdue → outstanding
      outstanding += Math.max(0, t.balance);
      if (s === 'overdue') overdueCount += 1;
    }
    return { outstanding, paidThisMonth, overdueCount };
  }, [storedInvoices]);
  const myTasks=tasks.filter(tk=>tk.status!=="done"&&(tk.assignedTo===currentUser?.id||!tk.assignedTo)).length;
  const renewSoon=policies.filter(p=>{ const d=daysUntil(p.renewalDate); return d!==null&&d>=0&&d<=30; }).length;
  const missingDocs=docs.filter(d=>d.required&&!d.uploadedAt).length;
  const activeClients=clients.filter(c=>c.status==="active").length;
  const cancelledCount=policies.filter(p=>p.status==="inactive"||p.status==="cancelled").length;
  const [showCancelled,setShowCancelled]=useState(false);
  const es = lang === "es";
  // Birthday alerts disappear once anyone has greeted THAT PERSON for THAT
  // birthday (Lissette 2026-05-27). The dedup question is now asked with the
  // shared key from ../greetings.js — this panel used to build its own
  // (`bday_<clientId>_<local today>`) while every writer stamped the UTC day
  // and the bare clientId, so an evening send or a spouse's greeting left the
  // alert sitting here in red (ticket 2026-08-14).
  //
  // A birthday still ahead keeps its heads-up row — it just carries `done` so
  // it renders as "✅ Programado" and stops being clickable, instead of
  // inviting the second send that produced the duplicate emails.
  const alerts=useMemo(()=>{
    const safeHasDone = typeof hasTeamActionBeenDone === "function" ? hasTeamActionBeenDone : () => ({ done: false });
    const list=[];
    // `isDep` gates the "turning 26" alert — aging off a parent's plan
    // only applies to a child dependent, never the primary client or
    // spouse (Lissette: 26 alert ONLY for dependents).
    const addPersonAlerts = (dob, name, clientId, isDep, person) => {
      if (!dob) return;
      const dBday = daysUntilBirthday(dob);
      const d65 = willTurnAge(dob, 65);
      const d26 = willTurnAge(dob, 26);

      // ── 1. BIRTHDAY: 3 days before, including family ──
      const personKey = person?.personKey || clientId;
      const dayKey = birthdayDayKey(dob);
      const greeted = safeHasDone(bdayGreetingKey(personKey, dayKey)).done;
      const bdayRow = { cat:"bday", color:"#f472b6", clientId, personKey, dayKey, person, daysUntil:dBday, done:greeted };
      if (dBday === 0 && !greeted) list.push({ ...bdayRow, icon:"🎉", msg: es ? `¡Hoy cumple años ${name}!` : `Today is ${name}'s birthday!`, urgent:true });
      else if (dBday === 1) list.push({ ...bdayRow, icon:"🎂", msg: es ? `¡Mañana cumple años ${name}!` : `${name}'s birthday is TOMORROW!`, urgent:true });
      else if (dBday === 2) list.push({ ...bdayRow, icon:"🎂", msg: es ? `${name} cumple años en 2 días` : `${name}'s birthday in 2 days`, urgent:false });
      else if (dBday === 3) list.push({ ...bdayRow, icon:"🎂", msg: es ? `${name} cumple años en 3 días` : `${name}'s birthday in 3 days`, urgent:false });

      // ── 2. TURNING 26: dependents only (ages off parent's plan) ──
      if (isDep && d26 !== null && d26 > 0 && d26 <= 365) {
        list.push({ cat:"age26", icon:"⚠️", tag: es?"MODIFICAR PLAN":"MODIFY PLAN", msg: es ? `${name} cumplirá 26 en ${d26} días — Se sale del plan de los padres` : `${name} turns 26 in ${d26} days — Will age off parent's plan`, color:"#fbbf24", urgent: d26 <= 90, clientId });
      }

      // ── 3. TURNING 65: two separate alerts ──
      // Alert A: 365 days before (info)
      if (d65 !== null && d65 > 0 && d65 <= 365 && d65 > 90) {
        list.push({ cat:"age65", icon:"🏥", tag:"MEDICARE", msg: es ? `${name} cumplirá 65 en ${d65} días` : `${name} will turn 65 in ${d65} days`, color:"#00d4ff", urgent:false, clientId });
      }
      // Alert B: 90 days before (URGENT)
      if (d65 !== null && d65 > 0 && d65 <= 90) {
        list.push({ cat:"age65u", icon:"🚨", tag: es?"MEDICARE URGENTE":"MEDICARE URGENT", msg: es ? `¡${name} cumple 65 en ${d65} días! — Inscribir en Medicare AHORA` : `${name} turns 65 in ${d65} days! — Enroll in Medicare NOW`, color:"#ff3d6b", urgent:true, clientId });
      }
    };

    clients.forEach(c => {
      const headName = `${c.firstName} ${c.lastName}`;
      addPersonAlerts(c.dob, headName, c.id, false, { personKey: bdayPersonKey(c.id), firstName: c.firstName, lastName: c.lastName, email: c.email, phone: c.phone, preferredLang: c.preferredLang });
      if (c.spouse?.dob && c.spouse.firstName) {
        const spName = `${c.spouse.firstName} ${c.spouse.lastName||c.lastName}`;
        addPersonAlerts(c.spouse.dob, es ? `${spName} — Cónyuge de ${headName}` : `${spName} — Spouse of ${headName}`, c.id, false, { personKey: bdayPersonKey(c.id, 'spouse'), firstName: c.spouse.firstName, lastName: c.spouse.lastName||c.lastName, email: c.spouse.email||c.email, phone: c.spouse.phone||c.phone, preferredLang: c.spouse.preferredLang||c.preferredLang, rel: es?'cónyuge':'spouse', ofName: headName });
      }
      (c.dependents||[]).forEach((dep, i) => {
        if(dep.dob && dep.firstName) {
          const depName = `${dep.firstName} ${dep.lastName||c.lastName}`;
          addPersonAlerts(dep.dob, es ? `${depName} — Dependiente de ${headName}` : `${depName} — Dependent of ${headName}`, c.id, true, { personKey: bdayPersonKey(c.id, 'dep', dep.id||i), firstName: dep.firstName, lastName: dep.lastName||c.lastName, email: dep.email||c.email, phone: dep.phone||c.phone, preferredLang: c.preferredLang, rel: es?'dependiente':'dependent', ofName: headName });
        }
      });
    });
    policies.forEach(p=>{ const d=daysUntil(p.renewalDate); const c=clients.find(cl=>cl.id===p.clientId); const pt=getPolicyType(p.type); if(d!==null&&d>=0&&d<=14&&c) list.push({ cat:"renewal", icon:pt.icon, msg:`${c.firstName} ${c.lastName} — ${es?pt.labelEs:pt.label} ${es?"renueva en":"renews in"} ${d}d`, color:"#ffb700", urgent:d<=7, clientId:c.id }); });
    docs.filter(d=>d.required&&!d.uploadedAt).forEach(d=>{ const c=clients.find(cl=>cl.id===d.clientId); if(c) list.push({ cat:"doc", icon:"📄", msg:`${c.firstName} ${c.lastName} — ${es?"falta":"missing"}: ${d.type}`, color:"#ff3d6b", urgent:false, clientId:c.id }); });
    // Eligibility document alerts
    (eligibilityAlerts||[]).filter(ea=>ea.status==="pending"&&ea.deadline).forEach(ea=>{
      const c=clients.find(cl=>cl.id===ea.clientId);
      if(!c) return;
      const [y,m,d2]=ea.deadline.split("-").map(Number);
      const target=new Date(y,m-1,d2); const today=new Date(); today.setHours(0,0,0,0);
      const dd=Math.ceil((target-today)/(1000*60*60*24));
      const docName=es?ea.doc:(ea.docEn||ea.doc);
      const isOverdue=dd<0;
      if(dd<=90||isOverdue) list.push({ cat:isOverdue?"eligOD":"elig", icon:isOverdue?"🚨":"📋", tag:es?"ELEGIBILIDAD":"ELIGIBILITY", msg:isOverdue?`${c.firstName} ${c.lastName} — ${docName} ${es?"VENCIDO":"OVERDUE"}`:`${c.firstName} ${c.lastName} — ${docName} (${dd} ${es?"días":"days"})`, color:isOverdue?"#ff3d6b":dd<=14?"#ffb700":"#00d4ff", urgent:isOverdue||dd<=14, clientId:c.id });
    });
    return list.sort((a,b) => { if(a.urgent&&!b.urgent) return -1; if(!a.urgent&&b.urgent) return 1; const o={age65u:0,eligOD:1,age65:2,age26:3,bday:4,renewal:5,elig:6,doc:7}; return (o[a.cat]||9)-(o[b.cat]||9); });
  // hasTeamActionBeenDone is a useCallback in Deskte() whose deps include
  // teamActions + agencyMates (deskte-full.jsx:6327). So this useMemo
  // re-runs when teammates send greetings — the just-saluted person
  // drops out of the alerts list on the next render.
  },[clients,policies,docs,eligibilityAlerts,hasTeamActionBeenDone]);
  const recent=[...clients].sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).slice(0,5);
  const goToClient = (cl) => {
    window.__magGoToClient = cl;
    setView("clients");
  };
  const recentChanges = useMemo(()=>{
    const seen = new Set();
    const result = [];
    const sorted = [...(changeHistory||[])].sort((a,b)=>new Date(b.date)-new Date(a.date));
    for(const h of sorted){
      if(seen.has(h.clientId)) continue;
      seen.add(h.clientId);
      const cl = clients.find(c=>c.id===h.clientId);
      if(cl) result.push({client:cl, entry:h});
      if(result.length>=5) break;
    }
    return result;
  },[changeHistory, clients]);
  const fmtDate = (iso) => {
    if(!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
  };
  // Empty-state onboarding for brand-new agent accounts. Fires when the
  // agent has zero clients AND zero policies — i.e. they just signed up
  // via the form from PR #65 and haven't touched the CRM yet. We render
  // the same 3 quick-start steps the welcome email surfaces so the
  // in-app flow and the email line up.
  //
  // PLATFORM_OWNER is excluded: by design they have no clients/policies
  // (they administer Deskte, they don't run a CRM book), so the
  // "add your first client" wizard makes no sense for them. They have
  // their own dedicated PlatformAdminView; if they wind up on the
  // Dashboard view by accident (e.g. typing the URL), show the
  // skeleton dashboard instead of the new-agent wizard.
  const isFreshSignup = !currentUser?.isPlatformOwner && clients.length === 0 && policies.length === 0;
  if (isFreshSignup) {
    return <OnboardingEmptyState lang={lang} firstName={currentUser?.name?.split(' ')[0] || ''} setView={setView} />;
  }

  return <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
      <div>
        <div className="dkt-sec-title">{td.title}</div>
        <div className="dkt-sec-sub">Welcome back, {currentUser?.name.split(" ")[0]} . {new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'.5rem',fontSize:'.68rem',color:'var(--c-text3,#45688a)',fontFamily:'var(--font-mono)'}}>
        <button
          type="button"
          onClick={() => setShowWidgetSettings(true)}
          title={lang === 'es' ? 'Personalizar dashboard' : 'Customize dashboard'}
          style={{background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:7,padding:'4px 9px',cursor:'pointer',color:'var(--c-text2,#7aa3c0)',fontSize:'.65rem',fontWeight:600,fontFamily:'var(--font-mono)',display:'inline-flex',alignItems:'center',gap:4}}
        >
          <Settings size={11} /> {lang === 'es' ? 'Personalizar' : 'Customize'}
        </button>
        <span style={{width:7,height:7,borderRadius:'50%',background:'var(--c-green,#00ff88)',display:'inline-block',boxShadow:'0 0 6px var(--c-green,#00ff88)'}}/>
        SYSTEM ONLINE
      </div>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'.75rem'}}>
      <StatCard icon={Users} label={td.totalClients} value={clients.length} accent="bg-blue-500" onClick={()=>{window.__magStatusFilter="all";setView("clients");}}/>
      <StatCard icon={CheckCircle} label={td.activeClients} value={activeClients} accent="bg-emerald-500" onClick={()=>{window.__magStatusFilter="active";setView("clients");}}/>
      <StatCard icon={UserX} label={lang==="es"?"Clientes Inactivos":"Inactive Clients"} value={clients.filter(c=>c.status==="inactive"||c.status==="cancelled").length} accent="bg-gray-500" onClick={()=>{window.__magStatusFilter="inactive";setView("clients");}}/>
      <StatCard icon={Star} label={lang==="es"?"Prospectos":"Prospects"} value={clients.filter(c=>c.status==="prospect").length} accent="bg-blue-500" onClick={()=>{window.__magStatusFilter="prospect";setView("clients");}}/>
      <StatCard icon={Gift} label={lang==="es"?"Referidos":"Referrals"} value={clients.filter(c=>c.status==="referral").length} accent="bg-emerald-500" onClick={()=>{window.__magStatusFilter="referral";setView("clients");}}/>
      <StatCard icon={Calendar} label={td.renewalsSoon} value={renewSoon} accent="bg-amber-500" onClick={()=>setView("renewals")}/>
      <StatCard icon={ListTodo} label={td.myTasks} value={myTasks} accent="bg-purple-500" onClick={()=>setView("tasks")}/>
      <StatCard icon={XCircle} label={lang==="es"?"Pólizas Canceladas":"Cancelled Policies"} value={cancelledCount} accent="bg-red-500" onClick={()=>setShowCancelled(true)}/>
      <StatCard icon={Ticket} label={lang==="es"?"Tickets abiertos":"Open tickets"} value={ticketsOpen} accent="bg-blue-500" onClick={()=>setView("tickets")}/>
      <StatCard icon={Send} label={lang==="es"?"Seguimiento":"Follow-ups"} value={fuOverdue} accent="bg-purple-500" onClick={()=>setView("followups")}/>
    </div>
    {(!isHidden('recent_clients') || !isHidden('pending_alerts')) && (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:'1rem'}}>
      {!isHidden('recent_clients') && (
      <div className="dkt-card" style={{padding:'1.1rem'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.875rem'}}>
          <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'.9rem',color:'var(--c-white,#eef6ff)'}}>{lang==="es"?"Clientes Recientes":"Recent Clients"}</span>
          <button onClick={()=>setView("clients")} style={{background:'none',border:'none',fontSize:'.7rem',color:'var(--c-cyan,#00d4ff)',cursor:'pointer'}}>{td.viewAll} {'->'}</button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'.4rem'}}>
          {recent.map(cl=>{ const cp=policies.filter(p=>p.clientId===cl.id);
            return <div key={cl.id}
              onClick={()=>goToClient(cl)}
              style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'.55rem .75rem',borderRadius:9,
                background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',
                cursor:'pointer',transition:'border-color .15s, background .15s'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--c-cyan,#00d4ff)';e.currentTarget.style.background='rgba(0,212,255,.04)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--c-border,#1a3550)';e.currentTarget.style.background='var(--c-surface2,#112538)';}}>
              <div style={{display:'flex',alignItems:'center',gap:'.6rem'}}>
                <div style={{position:'relative'}}><Avatar client={cl} size={8}/>{birthdayToday(cl.dob)&&<span style={{position:'absolute',top:-3,right:-3,fontSize:10}}>🎂</span>}</div>
                <div>
                  <div style={{fontSize:'.82rem',fontWeight:600,color:'var(--c-cyan,#00d4ff)'}}>{cl.firstName} {cl.lastName}</div>
                  <div style={{display:'flex',gap:2,fontSize:12,marginTop:1}}>{cp.slice(0,5).map(p=><span key={p.id}>{getPolicyType(p.type).icon}</span>)}</div>
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
                <Badge status={cl.status} t={t}/>
                <span style={{fontSize:'.65rem',color:'var(--c-text3,#45688a)'}}>›</span>
              </div>
            </div>;
          })}
        </div>
      </div>
      )}
      {!isHidden('pending_alerts') && (
      <div className="dkt-card" style={{padding:'1.1rem'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.875rem'}}>
          <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'.9rem',color:'var(--c-white,#eef6ff)'}}>{td.pendingAlerts}</span>
          <Bell size={14} style={{color:'var(--c-text3,#45688a)'}}/>
        </div>
        {alerts.length===0&&<div style={{textAlign:'center',padding:'2rem 0',fontSize:'.78rem',color:'var(--c-text3,#45688a)'}}>{td.noAlerts} ✓</div>}
        <div style={{display:'flex',flexDirection:'column',gap:'.4rem',maxHeight:420,overflowY:'auto'}}>
          {alerts.map((a,i)=>{
            const isBday = a.cat==='bday';
            // A greeting already sent/queued for this person's birthday: keep
            // the heads-up visible but inert, so nobody "reprocesses" it into a
            // duplicate email (ticket 2026-08-14).
            const bdayHandled = isBday && a.done;
            return <div key={i} onClick={()=>{
                if(bdayHandled) return;
                const cl = a.clientId ? clients.find(c=>c.id===a.clientId) : null;
                if(!cl) return;
                // Birthday alerts open the send/SCHEDULE greeting popup instead
                // of jumping to the client (Lissette 2026-06-19, foto 2). The
                // composer lives in HolidayGreetingsPanel and is reached via the
                // window trigger it registers; if that panel is hidden the
                // global is absent and we fall back to opening the client.
                // It receives the BIRTHDAY PERSON (a spouse/dependent keeps
                // their own name + email) plus the person/occurrence keys the
                // greeting marker is written under.
                const p = a.person || {};
                if(isBday && (p.email || cl.email) && typeof window.__dktOpenBdayCompose==='function'){
                  window.__dktOpenBdayCompose({ ...cl,
                    firstName: p.firstName || cl.firstName, lastName: p.lastName || cl.lastName,
                    email: p.email || cl.email, _ownEmail: p.email || '', phone: p.phone || cl.phone,
                    preferredLang: p.preferredLang || cl.preferredLang,
                    _bkey: a.personKey, _bdayKey: a.dayKey, _rel: p.rel || '', _ofName: p.ofName || '' });
                  return;
                }
                goToClient(cl);
              }}
              style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.55rem .75rem',borderRadius:9,
                background: a.urgent ? 'rgba(255,61,107,.06)' : 'var(--c-surface2,#112538)',
                border: `1px solid ${a.urgent ? 'rgba(255,61,107,.18)' : 'var(--c-border,#1a3550)'}`,
                borderLeft: `3px solid ${a.color}`,
                opacity: bdayHandled ? .7 : 1,
                cursor: (a.clientId && !bdayHandled) ? 'pointer' : 'default', transition:'background .15s'}}
              onMouseEnter={e=>{ if(a.clientId && !bdayHandled) e.currentTarget.style.background='var(--c-cyan-dim)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background=a.urgent?'rgba(255,61,107,.06)':'var(--c-surface2,#112538)'; }}
              title={bdayHandled ? (es?'Saludo de cumpleaños ya enviado o programado':'Birthday greeting already sent or scheduled') : isBday ? (es?'Clic para programar o enviar el mensaje de cumpleaños':'Click to schedule or send the birthday message') : undefined}>
              <span style={{fontSize:15,flexShrink:0}}>{a.icon}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:'.76rem',color:a.urgent?a.color:'var(--c-text,#c8dff0)',lineHeight:1.35,fontWeight:a.urgent?700:400}}>{a.msg}</div>
                {isBday && a.clientId && !bdayHandled && <div style={{fontSize:'.62rem',color:'#f472b6',opacity:.9,marginTop:2}}>{es?'Clic para programar o enviar el mensaje 🎂 →':'Click to schedule or send the message 🎂 →'}</div>}
                {a.tag && <span style={{display:'inline-block',marginTop:3,fontSize:'.52rem',padding:'1px 6px',borderRadius:4,fontWeight:800,fontFamily:'var(--font-mono)',letterSpacing:'.06em',background:`${a.color}18`,color:a.color,border:`1px solid ${a.color}30`}}>{a.tag}</span>}
              </div>
              {bdayHandled
                ? <span style={{fontSize:'.62rem',fontWeight:700,color:'var(--c-green,#00ff88)',flexShrink:0}}>✅ {a.daysUntil>0?(es?'Programado':'Scheduled'):(es?'Enviado':'Sent')}</span>
                : a.clientId && <span style={{fontSize:12,color:'var(--c-text3,#45688a)',flexShrink:0}}>{isBday?'📅':'›'}</span>}
            </div>;
          })}
        </div>
      </div>
      )}
    </div>
    )}
    {/* ── Holiday & Birthday Greetings Panel ── */}
    {!isHidden('greetings') && (
    <HolidayGreetingsPanel clients={clients} currentUser={currentUser} lang={lang} holidayTemplates={holidayTemplates} emailTemplates={emailTemplates} hasTeamActionBeenDone={hasTeamActionBeenDone} teamActionKey={teamActionKey} logTeamAction={logTeamAction} forgetTeamAction={forgetTeamAction} confirmBeforeResend={confirmBeforeResend}/>
    )}
    {showCancelled&&<CancelledPoliciesView policies={policies} clients={clients} onClose={()=>setShowCancelled(false)} t={t} lang={lang}/>}
    {recentChanges.length>0 && !isHidden('recent_changes') && <div className="dkt-card" style={{padding:'1.1rem'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.875rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'.5rem'}}>
          <Clock size={14} style={{color:'var(--c-cyan,#00d4ff)'}}/>
          <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'.9rem',color:'var(--c-white,#eef6ff)'}}>{lang==="es"?"Cambios Recientes":"Recent Changes"}</span>
        </div>
        <button onClick={()=>setView("clients")} style={{background:'none',border:'none',fontSize:'.7rem',color:'var(--c-cyan,#00d4ff)',cursor:'pointer'}}>{td.viewAll} {'->'}</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'.5rem'}}>
        {recentChanges.map(({client:cl, entry:h})=>{
          const PTICONS={aca:"🏥",medicare:"💊",dental:"🦷",vision:"👁️",life:"❤️",auto:"🚗",home:"🏠",renters:"🏘️",commercial:"🏢"};
          const typeIcon = h.type==="policy"?(PTICONS[h.policyType]||"📋"):"👤";
          const changeDesc = h.changes.slice(0,2).map(ch=>ch.label).join(", ")+(h.changes.length>2?` +${h.changes.length-2}`:"");
          return <div key={h.id}
            onClick={()=>goToClient(cl)}
            style={{display:'flex',alignItems:'center',gap:'.65rem',padding:'.6rem .75rem',
              borderRadius:9,background:'var(--c-surface2,#112538)',
              border:'1px solid var(--c-border,#1a3550)',cursor:'pointer',transition:'border-color .15s,background .15s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--c-cyan,#00d4ff)';e.currentTarget.style.background='rgba(0,212,255,.04)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--c-border,#1a3550)';e.currentTarget.style.background='var(--c-surface2,#112538)';}}>
            <div style={{width:34,height:34,borderRadius:9,background:'rgba(0,212,255,.08)',border:'1px solid rgba(0,212,255,.15)',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',flexShrink:0}}>
              {typeIcon}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:'.82rem',fontWeight:700,color:'var(--c-cyan,#00d4ff)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                {cl.firstName} {cl.lastName}
              </div>
              <div style={{fontSize:'.68rem',color:'var(--c-text3,#45688a)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',marginTop:1}}>
                {changeDesc}
              </div>
              <div style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)',marginTop:2}}>
                {fmtDate(h.date)} . {h.user}
              </div>
            </div>
            <span style={{fontSize:'.7rem',color:'var(--c-text3,#45688a)',flexShrink:0}}>›</span>
          </div>;
        })}
      </div>
    </div>}

    {/* ── DAILY ACTIVITY FEED ─────────────────────────── */}
    {!isHidden('activity_feed') && <ActivityFeedPanel activitylog={activitylog} clients={clients} policies={policies} lang={lang} onOpenClient={goToClient} />}
    {/* Customize-dashboard modal — checkboxes for each widget id. */}
    {showWidgetSettings && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={() => setShowWidgetSettings(false)}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--c-border,#1a3550)' }}>
            <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
              {lang === 'es' ? '⚙️ Personalizar Dashboard' : '⚙️ Customize Dashboard'}
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
              {lang === 'es'
                ? 'Activa o desactiva los paneles que quieres ver en tu dashboard. Se guarda por usuario.'
                : 'Toggle the panels you want on your dashboard. Saved per user.'}
            </div>
          </div>
          <div style={{ padding: '.5rem 0' }}>
            {DASHBOARD_WIDGETS.map((w) => {
              const visible = !isHidden(w.id);
              return (
                <label
                  key={w.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '.65rem 1.25rem', cursor: 'pointer', transition: 'background .15s',
                    fontSize: '.85rem', color: visible ? 'var(--c-white,#eef6ff)' : 'var(--c-text3,#45688a)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{lang === 'es' ? w.labelEs : w.labelEn}</span>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => toggleWidget(w.id)}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--c-cyan,#00d4ff)' }}
                  />
                </label>
              );
            })}
          </div>
          <div style={{ padding: '.75rem 1.25rem', borderTop: '1px solid var(--c-border,#1a3550)', display: 'flex', gap: '.5rem', justifyContent: 'space-between' }}>
            <button type="button" onClick={resetWidgets} style={{ padding: '.5rem .9rem', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' }}>
              {lang === 'es' ? 'Mostrar todos' : 'Show all'}
            </button>
            <button type="button" onClick={() => setShowWidgetSettings(false)} style={{ padding: '.5rem 1.25rem', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#0099cc,#0055aa)', color: '#fff', fontSize: '.82rem', fontWeight: 800, cursor: 'pointer' }}>
              {lang === 'es' ? 'Listo' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

// Activity feed with a date picker so the user can navigate to past days.
// The selected day is fetched STRAIGHT FROM THE SERVER (not just filtered
// from the in-memory ~100-row cache) so: (1) any past date loads its real
// rows from the DB — no more "it looks erased when I go back"; (2) "today"
// loads the WHOLE day (not capped by the rolling 100 most-recent rows shared
// across all days), so a teammate's / seat's actions reliably show up. The
// in-memory `activitylog` is still merged in for the live (SSE) tail so brand
// new actions appear instantly without waiting for the next fetch.
function ActivityFeedPanel({ activitylog, clients, policies, lang, onOpenClient }) {
  const todayKey = localDay();
  const [selectedDate, setSelectedDate] = useState(todayKey);
  // Server-fetched rows for the selected day. We convert the user's LOCAL day
  // boundaries to UTC instants so the window matches their wall clock even
  // when the API server runs in UTC.
  const [serverEntries, setServerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  // #10 (Lissette 2026-07-08): click a person chip to see ONLY that person's
  // activity for the day. null = everyone. Reset whenever the day changes.
  const [userFilter, setUserFilter] = useState(null);
  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    setLoading(true);
    const dayStart = new Date(selectedDate + 'T00:00:00');
    const dayEnd = new Date(selectedDate + 'T23:59:59.999');
    listActivity({ from: dayStart.toISOString(), to: dayEnd.toISOString(), limit: 1000 })
      .then(({ activity }) => { if (!cancelled) setServerEntries(activity || []); })
      .catch((err) => {
        if (cancelled) return;
        console.error('[activity] day fetch failed:', err.message);
        setServerEntries([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDate]);
  const isToday = selectedDate === todayKey;
  // PHI read-access events (every SSN reveal logs `phi.<field>.decrypted`,
  // plus ssn.reveal*) MUST stay in the DB / admin audit for HIPAA, but
  // Lissette doesn't want one row per *view* spamming this feed — only
  // real changes (incl. ssn.edited). Hide them from the UI only.
  const isPhiAccessEvent = (action) => {
    const a = String(action || "");
    return a.startsWith("phi.") || a === "ssn.reveal" || a === "ssn.reveal_denied";
  };
  // Day-of-the-feed entries — server rows for the selected day MERGED with the
  // live in-memory cache for that same day (so SSE-appended actions show
  // instantly). Dedup by id, server row wins (it carries full user/details).
  // Cap at 300 so a very active day doesn't blow up the DOM.
  const dayEntries = useMemo(() => {
    const dayMatch = (a) =>
      a && a.createdAt && !isPhiAccessEvent(a.action) && localDay(a.createdAt) === selectedDate;
    const byId = new Map();
    for (const a of serverEntries) if (dayMatch(a)) byId.set(a.id, a);
    for (const a of (activitylog || [])) if (dayMatch(a) && !byId.has(a.id)) byId.set(a.id, a);
    return Array.from(byId.values())
      .sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt))
      .slice(0, 300);
  }, [serverEntries, activitylog, selectedDate]);
  // #10: the per-person key used both for the chips and for the click-to-filter.
  const userKeyOf = (a) => a.userName || a.userId || "unknown";
  // Changing the day clears any person filter (its chip may not exist that day).
  useEffect(() => { setUserFilter(null); }, [selectedDate]);
  // Entries actually rendered: everyone, or just the picked person.
  const shownEntries = userFilter ? dayEntries.filter((a) => userKeyOf(a) === userFilter) : dayEntries;
  const ENTITY_LABELS_ES = { client: "cliente", policy: "póliza", task: "tarea", lead: "lead", appointment: "cita", note: "nota", commission: "comisión", document: "documento", signature: "firma", user: "usuario", scheduled_send: "correo programado" };
  const ENTITY_LABELS_EN = { client: "client", policy: "policy", task: "task", lead: "lead", appointment: "appointment", note: "note", commission: "commission", document: "document", signature: "signature", user: "user", scheduled_send: "scheduled email" };
  const ACTION_LABELS_ES = { created: "creó", updated: "actualizó", deleted: "eliminó", registered: "registró", completed: "completó", login: "ingresó", signature_sent: "envió a firmar", signature_signed: "firmó", "scheduled_send.queued": "programó", "scheduled_send.sent": "envió", "scheduled_send.cancelled": "canceló" };
  const ACTION_LABELS_EN = { created: "created", updated: "updated", deleted: "deleted", registered: "registered", completed: "completed", login: "logged in", signature_sent: "sent for signature", signature_signed: "signed", "scheduled_send.queued": "scheduled", "scheduled_send.sent": "sent", "scheduled_send.cancelled": "cancelled" };
  const ACTION_ICON = { created: "✚", updated: "✎", deleted: "✕", registered: "★", completed: "✓", login: "→", signature_sent: "✍︎", signature_signed: "✔︎" };
  const ACTION_COLOR = { created: "#10b981", updated: "#06b6d4", deleted: "#ef4444", registered: "#f59e0b", completed: "#8b5cf6", login: "#64748b", signature_sent: "#0099cc", signature_signed: "#10b981" };
  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString(lang === "es" ? "es" : "en", { hour: "numeric", minute: "2-digit", hour12: true });
  };
  // Resolve the subject of an entry to a human label. Prefer the snapshot
  // the backend now stores in `details` (survives soft-delete); fall back
  // to resolving entityId against the in-memory lists for older logs.
  const clientById = useMemo(() => {
    const m = {}; (clients || []).forEach(c => { m[c.id] = c; }); return m;
  }, [clients]);
  const policyById = useMemo(() => {
    const m = {}; (policies || []).forEach(p => { m[p.id] = p; }); return m;
  }, [policies]);
  // Resolve which client an activity points at so the row can deep-link to that
  // client's profile (where the policy/task/signature/note/appointment lives).
  // Client rows use entityId; everything else carries details.clientId from the
  // backend; policies fall back to resolving the policy → its client.
  const rowClient = (a) => {
    if (!a) return null;
    let cid = null;
    if (a.entityType === "client") cid = a.entityId || (a.details && a.details.clientId);
    else if (a.details && a.details.clientId) cid = a.details.clientId;
    else if (a.entityType === "policy") { const p = policyById[a.entityId]; if (p) cid = p.clientId; }
    return cid ? (clientById[cid] || null) : null;
  };
  const describe = (a) => {
    const d = a.details || {};
    if (a.entityType === "client") {
      const c = clientById[a.entityId];
      return d.name || (c ? `${c.firstName} ${c.lastName}`.trim() : "");
    }
    if (a.entityType === "policy") {
      const p = policyById[a.entityId];
      const type = d.type || p?.type || "";
      const carrier = d.carrier || p?.carrier || "";
      const num = d.policyNumber || p?.policyNumber || "";
      let clientName = d.clientName || "";
      if (!clientName && p) {
        const pc = clientById[p.clientId];
        if (pc) clientName = `${pc.firstName} ${pc.lastName}`.trim();
      }
      const parts = [type, carrier].filter(Boolean).join(" · ");
      const tail = [parts || null, num ? `#${num}` : null].filter(Boolean).join(" ");
      return [tail || null, clientName || null].filter(Boolean).join(" — ");
    }
    if (a.entityType === "signature") {
      // Backend writes details.clientName + details.documentName when a
      // signature is sent/signed; the feed row should read e.g.
      // "Carlos Méndez — Form HIPAA".
      const clientName = d.clientName || "";
      const docName = d.documentName || d.name || "";
      return [clientName || null, docName || null].filter(Boolean).join(" — ");
    }
    if (a.entityType === "scheduled_send") {
      // Name the client the scheduled email is for, so the feed reads
      // "envió el saludo de cumpleaños — Nicolas Alarcon" instead of a bare
      // "scheduled_send" with no subject (Lissette 2026-06-22).
      const c = clientById[d.clientId];
      return c ? `${c.firstName} ${c.lastName}`.trim() : (d.clientName || "");
    }
    return d.name || d.title || "";
  };
  const total = dayEntries.length;
  const byUser = {};
  dayEntries.forEach(a => {
    const k = a.userName || a.userId || "unknown";
    byUser[k] = (byUser[k] || 0) + 1;
  });
  // Title varies by whether the date is today, yesterday, or a back-dated
  // browse. "Today's Activity" reads naturally; older days get a long-form
  // date stamp.
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return localDay(d); })();
  const title = isToday
    ? (lang === "es" ? "Actividad de Hoy" : "Today's Activity")
    : selectedDate === yesterday
      ? (lang === "es" ? "Actividad de Ayer" : "Yesterday's Activity")
      : (lang === "es" ? "Actividad" : "Activity");
  // Pretty-printed date label for the right side of the header. Uses the
  // selected date, not "today", so the user sees the day they're viewing.
  const dateLabel = new Date(selectedDate + 'T00:00:00').toLocaleDateString(
    lang === "es" ? "es" : "en",
    { weekday: "long", month: "short", day: "numeric" }
  );
  return <div className="dkt-card" style={{padding:'1.1rem',marginTop:'.5rem'}}>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'.875rem',flexWrap:'wrap',gap:'.5rem'}}>
      <div style={{display:'flex',alignItems:'center',gap:'.5rem',flexWrap:'wrap'}}>
        <Activity size={16} style={{color:'var(--c-cyan,#00d4ff)'}}/>
        <span style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:'.9rem',color:'var(--c-white,#eef6ff)'}}>
          {title}
        </span>
        <span style={{fontSize:'.7rem',color:'var(--c-text3,#45688a)',fontFamily:'var(--font-mono)'}}>
          · {userFilter ? `${shownEntries.length} ${lang === "es" ? "de" : "of"} ${total}` : total} {lang === "es" ? "acciones" : "actions"}
        </span>
        {loading && (
          <span style={{fontSize:'.65rem',color:'var(--c-cyan,#00d4ff)',fontFamily:'var(--font-mono)',display:'inline-flex',alignItems:'center',gap:'.25rem'}}>
            <RefreshCw size={11} className="dkt-spin"/> {lang === "es" ? "cargando…" : "loading…"}
          </span>
        )}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:'.4rem'}}>
        {!isToday && (
          <button
            type="button"
            onClick={() => setSelectedDate(todayKey)}
            style={{padding:'.2rem .5rem',borderRadius:6,border:'1px solid var(--c-cyan,#00d4ff)',background:'rgba(0,212,255,.1)',color:'var(--c-cyan,#00d4ff)',fontSize:'.65rem',fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
            {lang === "es" ? "Hoy" : "Today"}
          </button>
        )}
        <DateField
          value={selectedDate}
          max={todayKey}
          onChange={(e) => setSelectedDate(e.target.value || todayKey)}
          style={{width:140,padding:'.2rem .4rem',borderRadius:6,border:'1px solid var(--c-border,#1a3550)',background:'var(--c-surface2,#112538)',color:'var(--c-text2,#7aa3c0)',fontSize:'.7rem',fontFamily:'var(--font-mono)',cursor:'pointer'}}
        />
        <span style={{fontSize:'.65rem',color:'var(--c-text3,#45688a)',fontFamily:'var(--font-mono)'}}>{dateLabel}</span>
      </div>
    </div>

    {Object.keys(byUser).length > 0 && (
      <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',marginBottom:'.75rem',alignItems:'center'}}>
        {Object.entries(byUser).map(([name, count]) => {
          const active = userFilter === name;
          return (
            <button key={name} type="button"
              onClick={() => setUserFilter(active ? null : name)}
              title={active ? (lang === "es" ? "Ver todos" : "Show all") : (lang === "es" ? `Ver solo la actividad de ${name}` : `Show only ${name}'s activity`)}
              style={{padding:'.25rem .6rem',borderRadius:6,cursor:'pointer',fontFamily:'inherit',fontSize:'.7rem',transition:'all .15s',
                background: active ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'var(--c-surface2,#112538)',
                border: `1px solid ${active ? 'rgba(0,212,255,.5)' : 'var(--c-border,#1a3550)'}`,
                color: active ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)'}}>
              {active && '✓ '}<strong style={{color:'var(--c-cyan,#00d4ff)'}}>{name}</strong> · {count}
            </button>
          );
        })}
        {userFilter && (
          <button type="button" onClick={() => setUserFilter(null)}
            style={{padding:'.25rem .55rem',borderRadius:6,cursor:'pointer',fontFamily:'inherit',fontSize:'.68rem',fontWeight:700,
              background:'transparent',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text3,#45688a)'}}>
            ✕ {lang === "es" ? "Ver todos" : "Show all"}
          </button>
        )}
      </div>
    )}

    {shownEntries.length === 0 ? (
      <div style={{textAlign:'center',padding:'1.25rem',color:'var(--c-text3,#45688a)',fontSize:'.8rem',fontStyle:'italic'}}>
        {loading
          ? (lang === "es" ? "Cargando actividad…" : "Loading activity…")
          : userFilter
            ? (lang === "es" ? `Sin actividad de ${userFilter} en esta fecha.` : `No activity from ${userFilter} on this date.`)
            : isToday
              ? (lang === "es" ? "Aún no hay actividad hoy. Las acciones aparecerán aquí en tiempo real." : "No activity yet today. Actions will appear here in real time.")
              : (lang === "es" ? "Sin actividad en esta fecha." : "No activity on this date.")}
      </div>
    ) : (
      <div style={{display:'flex',flexDirection:'column',gap:'.4rem',maxHeight:320,overflowY:'auto'}}>
        {shownEntries.map(a => {
          // Friendly label for the WhatsApp/chat paste sync — the raw action
          // ("paste.conversation_appended") leaked into the feed as gibberish.
          // Render it as "actualizó el chat de WhatsApp — <cliente>" instead.
          const isChatUpdate = a.action === 'paste.conversation_appended';
          // Scheduled sends read as a self-contained phrase ("envió el saludo de
          // cumpleaños") with the client as the detail — no bare "scheduled_send".
          const isScheduledSend = typeof a.action === 'string' && a.action.startsWith('scheduled_send.');
          const chatChannel = (a.details && a.details.channel) || '';
          const schedSource = (a.details && a.details.source) || '';
          const schedWhat = lang === "es"
            ? (schedSource === 'birthday_greeting' ? 'el saludo de cumpleaños' : schedSource === 'holiday_greeting' ? 'el saludo de fiesta' : 'el correo')
            : (schedSource === 'birthday_greeting' ? 'the birthday greeting' : schedSource === 'holiday_greeting' ? 'the holiday greeting' : 'the email');
          const schedVerb = lang === "es"
            ? (a.action === 'scheduled_send.sent' ? 'envió' : a.action === 'scheduled_send.cancelled' ? 'canceló' : 'programó')
            : (a.action === 'scheduled_send.sent' ? 'sent' : a.action === 'scheduled_send.cancelled' ? 'cancelled' : 'scheduled');
          const entity = lang === "es" ? (ENTITY_LABELS_ES[a.entityType] || a.entityType) : (ENTITY_LABELS_EN[a.entityType] || a.entityType);
          const action = isChatUpdate
            ? (chatChannel === 'whatsapp'
                ? (lang === "es" ? "actualizó el chat de WhatsApp" : "updated the WhatsApp chat")
                : (lang === "es" ? "actualizó el chat" : "updated the chat"))
            : isScheduledSend
              ? `${schedVerb} ${schedWhat}`
              : (lang === "es" ? (ACTION_LABELS_ES[a.action] || a.action) : (ACTION_LABELS_EN[a.action] || a.action));
          const icon = isChatUpdate ? "💬" : isScheduledSend ? (a.action === 'scheduled_send.sent' ? "📨" : "📅") : (ACTION_ICON[a.action] || "•");
          const color = isChatUpdate ? "#25D366" : isScheduledSend ? "#f472b6" : (ACTION_COLOR[a.action] || "#64748b");
          const userName = a.userName || (lang === "es" ? "Sistema" : "System");
          const detail = describe(a);
          const cl = onOpenClient ? rowClient(a) : null;
          const clickable = !!cl;
          return <div key={a.id}
            onClick={clickable ? () => onOpenClient(cl) : undefined}
            title={clickable ? `${lang === "es" ? "Abrir" : "Open"} ${cl.firstName || ""} ${cl.lastName || ""}`.trim() : undefined}
            onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = 'var(--c-cyan,#00d4ff)'; e.currentTarget.style.background = 'rgba(0,212,255,.08)'; } : undefined}
            onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; e.currentTarget.style.background = 'var(--c-surface2,#112538)'; } : undefined}
            style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.5rem .7rem',borderRadius:8,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',cursor:clickable?'pointer':'default',transition:'background .12s, border-color .12s'}}>
            <div style={{width:24,height:24,borderRadius:'50%',background:color+'22',display:'flex',alignItems:'center',justifyContent:'center',color,fontWeight:700,fontSize:'.85rem',flexShrink:0}}>{icon}</div>
            <div style={{flex:1,minWidth:0,fontSize:'.78rem',color:'var(--c-white,#eef6ff)'}}>
              <strong style={{color:'var(--c-cyan,#00d4ff)'}}>{userName}</strong>
              <span style={{color:'var(--c-text2,#7aa3c0)'}}> {action} </span>
              {!isChatUpdate && !isScheduledSend && <>
                <span style={{color:'var(--c-text2,#7aa3c0)'}}>{lang === "es" ? "un" : "a"} </span>
                <strong>{entity}</strong>
              </>}
              {detail && <span style={{color:'var(--c-text2,#7aa3c0)'}}> — <strong style={{color:'var(--c-white,#eef6ff)'}}>{detail}</strong></span>}
            </div>
            <span style={{fontSize:'.65rem',color:'var(--c-text3,#45688a)',fontFamily:'var(--font-mono)',flexShrink:0}}>
              {fmtTime(a.createdAt)}
            </span>
            {clickable && <ChevronRight size={13} style={{color:'var(--c-text3,#45688a)',flexShrink:0}}/>}
          </div>;
        })}
      </div>
    )}
  </div>;
}
// ═══════════════════════════════════════════════════════════
// COMPANY APPOINTMENTS VIEW
// ═══════════════════════════════════════════════════════════
export const APPT_DATA = {
  health: {
    label: "Health / ACA",
    carriers: ["Aetna","AmeriHealth Caritas","Ambetter","BCBS","Oscar","CareSource","Cigna","Highmark BCBS","Kaiser","Molina","United Health (U65)","Ascension"],
    states: [
      {s:"Alabama",c:{Ambetter:"Ambetter","United Health (U65)":"UHC HEALTH"}},
      {s:"Alaska",c:{}},
      {s:"Arizona",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"BCBS of AZ",Oscar:"Oscar",Cigna:"Cigna","United Health (U65)":"UHC HEALTH"}},
      {s:"Arkansas",c:{Ambetter:"Ambetter"}},
      {s:"California",c:{Aetna:"Aetna",Ambetter:"Healthnet",BCBS:"Anthem BC of CA",Kaiser:"Kaiser",Molina:"Molina"}},
      {s:"Colorado",c:{BCBS:"Anthem BCBS of CO",Cigna:"Cigna",Kaiser:"Kaiser","United Health (U65)":"UHC HEALTH"}},
      {s:"Connecticut",c:{BCBS:"Anthem BCBS of CT"}},
      {s:"Delaware",c:{Aetna:"Aetna","AmeriHealth Caritas":"AH Caritas",Ambetter:"Ambetter","Highmark BCBS":"Highmark"}},
      {s:"DC",c:{}},
      {s:"Florida",c:{Aetna:"Aetna","AmeriHealth Caritas":"AH Caritas",Ambetter:"Ambetter",Oscar:"Oscar",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH",Ascension:"Health First"}},
      {s:"Georgia",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Anthem BCBS of GA",Oscar:"Oscar",CareSource:"CareSource",Cigna:"Cigna",Kaiser:"Kaiser","United Health (U65)":"UHC HEALTH"}},
      {s:"Hawaii",c:{}},
      {s:"Idaho",c:{BCBS:"Regence BCBS of ID",Molina:"Molina"}},
      {s:"Illinois",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"HCSC BCBS of IL",Oscar:"Oscar",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Indiana",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Anthem BCBS of IN",CareSource:"CareSource",Cigna:"Cigna","United Health (U65)":"UHC HEALTH"}},
      {s:"Iowa",c:{Ambetter:"Ambetter",Oscar:"Oscar","United Health (U65)":"UHC HEALTH"}},
      {s:"Kansas",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"BlueKC / BCBS of KA",Oscar:"Oscar","United Health (U65)":"UHC HEALTH"}},
      {s:"Kentucky",c:{Ambetter:"Ambetter",BCBS:"Anthem BCBS of KY",CareSource:"CareSource",Molina:"Molina"}},
      {s:"Louisiana",c:{Ambetter:"Ambetter","United Health (U65)":"UHC HEALTH"}},
      {s:"Maine",c:{BCBS:"Anthem BCBS of ME"}},
      {s:"Maryland",c:{Aetna:"Aetna",Kaiser:"Kaiser","United Health (U65)":"UHC HEALTH"}},
      {s:"Massachusetts",c:{}},
      {s:"Michigan",c:{Ambetter:"Ambetter",BCBS:"BCBS of MI",Oscar:"Oscar",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Minnesota",c:{}},
      {s:"Mississippi",c:{Ambetter:"Ambetter",BCBS:"BCBS of MS",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Missouri",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Anthem BCBS of MO",Oscar:"Oscar","United Health (U65)":"UHC HEALTH"}},
      {s:"Montana",c:{}},
      {s:"Nebraska",c:{Ambetter:"Ambetter",Oscar:"Oscar","United Health (U65)":"UHC HEALTH"}},
      {s:"Nevada",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Anthem BCBS of NV",Molina:"Molina"}},
      {s:"New Hampshire",c:{Ambetter:"Ambetter",BCBS:"Anthem BCBS of NH"}},
      {s:"New Jersey",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Horizon BCBS NJ",Oscar:"Oscar",Cigna:"Cigna","United Health (U65)":"UHC HEALTH"}},
      {s:"New Mexico",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"HCSC BCBS of NM",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"New York",c:{BCBS:"Anthem BCBS of NY",Oscar:"Oscar","Highmark BCBS":"Highmark","United Health (U65)":"UHC HEALTH"}},
      {s:"North Carolina",c:{Aetna:"Aetna","AmeriHealth Caritas":"AH Caritas",Ambetter:"Ambetter",Oscar:"Oscar",CareSource:"CareSource",Cigna:"Cigna","United Health (U65)":"UHC HEALTH"}},
      {s:"North Dakota",c:{}},
      {s:"Ohio",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"Anthem BCBS of OH",Oscar:"Oscar",CareSource:"CareSource",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Oklahoma",c:{Ambetter:"Ambetter",BCBS:"HCSC BCBS of OK",Oscar:"Oscar","United Health (U65)":"UHC HEALTH"}},
      {s:"Oregon",c:{BCBS:"Regence BCBS of OR",Cigna:"Cigna"}},
      {s:"Pennsylvania",c:{Ambetter:"Ambetter",BCBS:"Capital BlueCross",Oscar:"Oscar",Cigna:"Cigna","Highmark BCBS":"Highmark"}},
      {s:"Rhode Island",c:{}},
      {s:"South Carolina",c:{"AmeriHealth Caritas":"AH Caritas",Ambetter:"Ambetter",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"South Dakota",c:{}},
      {s:"Tennessee",c:{Ambetter:"Ambetter",BCBS:"BCBS of TN",Oscar:"Oscar",Cigna:"Cigna","United Health (U65)":"UHC HEALTH"}},
      {s:"Texas",c:{Aetna:"Aetna",Ambetter:"Ambetter",BCBS:"HCSC BCBS of TX",Oscar:"Oscar",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Utah",c:{Aetna:"Aetna",BCBS:"Regence BCBS of UT",Cigna:"Cigna",Molina:"Molina"}},
      {s:"Vermont",c:{}},
      {s:"Virginia",c:{Aetna:"Aetna",BCBS:"Anthem BCBS of VA",Oscar:"Oscar",Cigna:"Cigna",Kaiser:"Kaiser","United Health (U65)":"UHC HEALTH"}},
      {s:"Washington",c:{Ambetter:"Ambetter",BCBS:"Regence BCBS / Premera BC",Kaiser:"Kaiser",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"West Virginia",c:{Aetna:"Aetna",CareSource:"CareSource","Highmark BCBS":"Highmark"}},
      {s:"Wisconsin",c:{BCBS:"Anthem BCBS of WI",Cigna:"Cigna",Molina:"Molina","United Health (U65)":"UHC HEALTH"}},
      {s:"Wyoming",c:{"United Health (U65)":"UHC HEALTH"}},
    ]
  },
  auto: {
    label: "Auto",
    carriers: ["Progressive","GEICO","State Farm","Allstate","Liberty Mutual","Nationwide","Travelers","USAA","Farmers","Bristol West","Mercury","Safeco","National General","Kemper","Infinity"],
    states: ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","DC","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s=>({s,c:{}}))
  },
  home: {
    label: "Home / Property",
    carriers: ["State Farm","Allstate","Liberty Mutual","Nationwide","Travelers","USAA","Farmers","American Family","Erie Insurance","Auto-Owners","Safeco","Hippo","Lemonade","Citizens","Universal Property","Heritage","Federated National"],
    states: ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","DC","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s=>({s,c:{}}))
  },
  life: {
    label: "Life",
    carriers: ["Prudential","MetLife","New York Life","Northwestern Mutual","Lincoln Financial","Transamerica","Mutual of Omaha","Pacific Life","Protective","AIG","Globe Life","Nationwide","Principal","Securian","John Hancock"],
    states: ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","DC","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s=>({s,c:{}}))
  },
  medicare: {
    label: "Medicare / Supplement",
    carriers: ["Aetna","Humana","UnitedHealthcare","Cigna","Mutual of Omaha","WellCare","Anthem BCBS","Blue Cross Blue Shield","Centene","Molina","Devoted Health","Clover Health","Alignment Healthcare"],
    states: ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","DC","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s=>({s,c:{}}))
  }
};

// First-run dashboard: when an agency has zero clients and zero policies,
// the regular dashboard reads as empty walls of zeros and "—". Instead
// surface the same 3 quick-start steps from the welcome email, with
// in-app buttons that switch the view (no email round-trip needed).
function OnboardingEmptyState({ lang, firstName, setView }) {
  const es = lang === 'es';

  const steps = es ? [
    {
      icon: '👥',
      title: '1. Agrega tu primer cliente',
      body: 'Cliquea abajo para crear el primer cliente. También puedes importar un CSV o Excel desde la pestaña Clientes.',
      cta: 'Agregar cliente',
      view: 'clients',
    },
    {
      icon: '📧',
      title: '2. Conecta tu correo',
      body: 'En un click, conecta tu Gmail o Outlook. Los emails saldrán desde tu dirección real y los clientes te responden a ti directo.',
      cta: 'Conectar correo',
      view: 'my_email',
    },
    {
      icon: '🔒',
      title: '3. Configura tu PIN de seguridad',
      body: 'Tu PIN de 6 dígitos protege la revelación de SSN, tarjetas y cuentas bancarias. Es requisito HIPAA. 30 segundos.',
      cta: 'Crear PIN',
      view: 'profile',
    },
  ] : [
    {
      icon: '👥',
      title: '1. Add your first client',
      body: 'Click below to create your first client. You can also import a CSV or Excel file from the Clients tab.',
      cta: 'Add client',
      view: 'clients',
    },
    {
      icon: '📧',
      title: '2. Connect your inbox',
      body: 'One click to connect Gmail or Outlook. Emails will go from your real address; clients reply directly to you.',
      cta: 'Connect inbox',
      view: 'my_email',
    },
    {
      icon: '🔒',
      title: '3. Set up your security PIN',
      body: 'Your 6-digit PIN protects SSN, card, and bank-account reveals. Required for HIPAA. Takes 30 seconds.',
      cta: 'Create PIN',
      view: 'profile',
    },
  ];

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem 0' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
        <div style={{ fontSize: '2.25rem', marginBottom: '.4rem' }}>👋</div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: '1.55rem',
          color: 'var(--c-white,#eef6ff)',
          margin: '0 0 .25rem',
        }}>
          {es ? `¡Bienvenido a Deskte, ${firstName || ''}!` : `Welcome to Deskte, ${firstName || ''}!`}
        </h1>
        <p style={{ fontSize: '.9rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, margin: 0 }}>
          {es
            ? 'Tres pasos rápidos y tu desk queda funcionando.'
            : 'Three quick steps and your desk is up and running.'}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
        {steps.map((s, i) => (
          <div key={s.view} style={{
            background: 'var(--c-surface,#0d1c2e)',
            border: '1px solid var(--c-border,#1a3550)',
            borderRadius: 14,
            padding: '1.1rem 1.25rem',
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-start',
          }}>
            <div style={{
              fontSize: '1.6rem',
              flexShrink: 0,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 10,
              background: 'var(--c-cyan-dim,rgba(0,212,255,.08))',
              border: '1px solid rgba(0,212,255,.2)',
            }}>{s.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: '.95rem',
                color: 'var(--c-white,#eef6ff)',
                marginBottom: '.2rem',
              }}>{s.title}</div>
              <div style={{
                fontSize: '.78rem',
                color: 'var(--c-text2,#7aa3c0)',
                lineHeight: 1.5,
                marginBottom: '.7rem',
              }}>{s.body}</div>
              <button
                type="button"
                onClick={() => setView && setView(s.view)}
                style={{
                  background: 'linear-gradient(135deg,#0099cc,#0055aa)',
                  border: 'none',
                  color: '#fff',
                  padding: '.45rem 1rem',
                  borderRadius: 8,
                  fontSize: '.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '.02em',
                }}
              >{s.cta} →</button>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: '1.5rem',
        textAlign: 'center',
        fontSize: '.7rem',
        color: 'var(--c-text3,#45688a)',
        lineHeight: 1.6,
      }}>
        {es
          ? <>¿Preguntas? Escríbenos a <a href="mailto:support@deskte.com" style={{ color: 'var(--c-cyan,#00d4ff)' }}>support@deskte.com</a>. También te llegó un email con estos pasos.</>
          : <>Questions? Email us at <a href="mailto:support@deskte.com" style={{ color: 'var(--c-cyan,#00d4ff)' }}>support@deskte.com</a>. You also got an email with these steps.</>}
      </div>
    </div>
  );
}

